import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    IconButton,
    FormControl,
    MenuItem,
    Popper,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClientDashboardLayout from "./ClientDashboardLayout";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import Divider from "@mui/material/Divider";

const SelectChevronIcon = (props) => (
    <ExpandMoreIcon
        {...props}
        sx={{
            fontSize: 16,
            ...(props?.sx || {}),
        }}
    />
);

const filterSelectMenuProps = {
    // Rendering the menu in-place avoids detached positioning when parent containers
    // apply transforms/zoom/stacking contexts.
    disablePortal: true,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    // Prefer scrolling inside the menu over moving the menu away from the anchor.
    // This keeps the menu visually attached even for long lists.
    marginThreshold: 8,
    PaperProps: {
        sx: {
            mt: 0.5,
            borderRadius: 2,
            boxShadow: "0 18px 45px rgba(15,23,42,0.12)",
            maxHeight: 360,
            overflowY: "auto",
        },
    },
};


const confidenceTokens = {
    High: { bg: "#E9F7EF", color: "#2E7D32" },
    Medium: { bg: "#FFF6E8", color: "#B7791F" },
    Low: { bg: "#FFF3E0", color: "#EF6C00" },
    Critical: { bg: "#FFEBEE", color: "#D32F2F" },
    Pending: { bg: "#F3F4F6", color: "#6B7280" },
};

const confidenceFilterOptionLabels = {
    all: "All (0–100%)",
    High: "High (≥ 85%)",
    Medium: "Medium (60–84%)",
    Low: "Low (40–59%)",
    Critical: "Critical (< 40%)"
};

const columns = [
    { key: "date",        label: "Date",        flex: 0.7, minWidth: { xs: 56, sm: 68, md: 72, lg: 80 } },
    { key: "account",     label: "Account",     flex: 1.2, minWidth: 0 },
    { key: "description", label: "Description", flex: 3,   minWidth: 0 },
    { key: "amount",      label: "Amount",      flex: 1,   minWidth: { xs: 72, sm: 92, md: 100, lg: 110 } },
    { key: "vendor",      label: "Vendor",      flex: 1.5, minWidth: 0 },
    { key: "category",    label: "Category",    flex: 1.5, minWidth: 0 },
    { key: "confidence",  label: "Confidence",  flex: 1,   minWidth: 0 },
    { key: "actions",     label: "Actions",     flex: 1,   minWidth: { xs: 72, sm: 92, md: 104, lg: 120 } },
];

// Suggestions are enabled implicitly when the backend includes suggestion fields.
// (No build-time gating; rely on presence of classification/vendor suggestion data.)
const suggestionsEnabled = true;

// Suggested values should render in italic via this shared sx.
// Apply it conditionally in the Vendor/Category cells.
const suggestedTextSx = { fontStyle: "italic" };

const WiderCategoryPopper = (props) => {
    const anchorEl = props?.anchorEl;
    const width = anchorEl ? anchorEl.clientWidth * 2 : undefined;
    return (
        <Popper
            {...props}
            placement="bottom-start"
            style={{
                ...(props?.style || {}),
                width,
            }}
        />
    );
};

const SummaryCard = styled(Card)(() => ({
    borderRadius: 10,
    boxShadow: "0 12px 24px rgba(15,23,42,0.08)",
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFFFFF",
}));

const ContentPanel = styled(Paper)(() => ({
    borderRadius: 16,
    border: "1px solid #E5E7EB",
    boxShadow: "0 18px 45px rgba(15,23,42,0.04)",
    backgroundColor: "#FFFFFF",
    padding: 24,
}));

const StyledTabs = styled(Tabs)(() => ({
    minHeight: "unset",
    "& .MuiTabs-indicator": {
        height: 2,
        borderRadius: 0,
        backgroundColor: "#4B5DFF",
    },
}));

const StyledTab = styled(Tab)(() => ({
    textTransform: "none",
    fontWeight: 600,
    fontSize: 15,
    minHeight: "unset",
    marginRight: 24,
    paddingBottom: 8,
    paddingTop: 6,
    color: "#8A94A6",
    "&.Mui-selected": {
        color: "#536DEC",
    },
}));

const checkboxSx = {
    color: "#C6CCE2",
    "&.Mui-checked": { color: "#4C63D7" },
    "& .MuiSvgIcon-root": { width: 20, height: 20, borderRadius: 2 },
};

const formatCurrency = (value) =>
    Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    }).format(value || 0);

const formatIsoDateLocal = (value) => {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const safeNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const percentChange = (currentValue, previousValue) => {
    const current = safeNumber(currentValue);
    const previous = safeNumber(previousValue);

    if (Math.abs(previous) < 0.000001) {
        if (Math.abs(current) < 0.000001) return 0;
        return 100;
    }

    return ((current - previous) / Math.abs(previous)) * 100;
};

// Temporarily hide the Bank Activity top summary metrics section.
// Flip to true when we want to re-enable rendering.
const SHOW_BANK_ACTIVITY_METRICS = false;

/** Parse any date string and return MM/DD/YYYY */
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T00:00:00` : dateStr;
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return dateStr;
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

const BankActivity = () => {
    const { customer_id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [resolvedCustomerId, setResolvedCustomerId] = useState(customer_id || null);
    const [transactions, setTransactions] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vendorNextCursor, setVendorNextCursor] = useState(null);
    const [vendorListLoading, setVendorListLoading] = useState(false);
    const [globalAccounts, setGlobalAccounts] = useState([]);
    const [globalAccountsLoading, setGlobalAccountsLoading] = useState(false);
    const [globalAccountsError, setGlobalAccountsError] = useState(null);
    const [selectedBankCashAccountId, setSelectedBankCashAccountId] = useState(null);
    const [availableBankAccounts, setAvailableBankAccounts] = useState([]);
    const [selectedBankAccountIds, setSelectedBankAccountIds] = useState([]);
    const [anyProcessing, setAnyProcessing] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [confidenceFilter, setConfidenceFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [vendorFilter, setVendorFilter] = useState("all");
    const [vendorFilterInput, setVendorFilterInput] = useState("");
    const [vendorFilterSearchOptions, setVendorFilterSearchOptions] = useState([]);
    const [vendorFilterSearchNextCursor, setVendorFilterSearchNextCursor] = useState(null);
    const [vendorFilterSearchLoading, setVendorFilterSearchLoading] = useState(false);
    const [tabValue, setTabValue] = useState("overview");
    const [periodFilter, setPeriodFilter] = useState("all");
    const [exporting, setExporting] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingRowId, setEditingRowId] = useState(null);
    const [editVendorOption, setEditVendorOption] = useState(null);
    const [editVendorInput, setEditVendorInput] = useState("");
    const [editCategoryOption, setEditCategoryOption] = useState(null);
    const [editCategoryInput, setEditCategoryInput] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);
    const [vendorUserTyped, setVendorUserTyped] = useState(false);
    const [categoryUserTyped, setCategoryUserTyped] = useState(false);

    // ── Column resize ────────────────────────────────────────────────────────
    const CHECKBOX_COL_W = 70; // fixed width of the leading checkbox column
    const MIN_COL_W      = 40; // minimum resizable column width

    const [columnWidths, setColumnWidths] = useState(() => {
        const totalFlex = columns.reduce((sum, col) => sum + (col.flex || 1), 0);
        return Object.fromEntries(
            columns.map((col) => [col.key, Math.max(MIN_COL_W, Math.round((col.flex || 1) / totalFlex * 920))])
        );
    });
    const columnWidthsRef = useRef(columnWidths);
    columnWidthsRef.current = columnWidths;

    // Track the scrollable container width so we can scale columns to fill it
    const tableContainerRef  = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // If the sum of raw column widths is less than the available space, scale
    // every column up proportionally so they fill the container.  Once the
    // user drags a column wider than the container the table switches to
    // horizontal-scroll mode automatically.
    const effectiveWidths = useMemo(() => {
        const totalRaw = Object.values(columnWidths).reduce((a, b) => a + b, 0);
        const available = containerWidth - CHECKBOX_COL_W;
        if (containerWidth <= 0 || totalRaw >= available) return columnWidths;
        const scale = available / totalRaw;
        return Object.fromEntries(
            Object.entries(columnWidths).map(([key, w]) => [key, Math.max(MIN_COL_W, Math.round(w * scale))])
        );
    }, [columnWidths, containerWidth]);

    const effectiveWidthsRef = useRef(effectiveWidths);
    effectiveWidthsRef.current = effectiveWidths;

    const startResize = useCallback((e, key) => {
        e.preventDefault();
        e.stopPropagation();
        const startX     = e.clientX;
        // Use the *visual* (possibly scaled) width so the mouse tracks correctly
        const startWidth = effectiveWidthsRef.current[key];
        const onMouseMove = (me) => {
            const newWidth = Math.max(MIN_COL_W, startWidth + me.clientX - startX);
            setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup',   onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup',   onMouseUp);
    }, []);
    // ────────────────────────────────────────────────────────────────────────

    const showSelectionActionBar = (selectedRows || []).length > 1;

    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const [bulkVendorOption, setBulkVendorOption] = useState(null);
    const [bulkVendorInput, setBulkVendorInput] = useState("");
    const [bulkVendorUserTyped, setBulkVendorUserTyped] = useState(false);
    const [bulkVendorSearchOptions, setBulkVendorSearchOptions] = useState([]);
    const [bulkVendorSearchLoading, setBulkVendorSearchLoading] = useState(false);

    const [bulkCategoryOption, setBulkCategoryOption] = useState(null);
    const [bulkCategoryInput, setBulkCategoryInput] = useState("");
    const [bulkCategoryUserTyped, setBulkCategoryUserTyped] = useState(false);
    const [bulkSaving, setBulkSaving] = useState(false);

    const [vendorSearchOptions, setVendorSearchOptions] = useState([]);
    const [vendorSearchLoading, setVendorSearchLoading] = useState(false);

    const [createVendorOpen, setCreateVendorOpen] = useState(false);
    const [createVendorName, setCreateVendorName] = useState("");
    const [createVendorAccount, setCreateVendorAccount] = useState(null);
    const [createVendorSaving, setCreateVendorSaving] = useState(false);
    const [createVendorError, setCreateVendorError] = useState(null);
    const [createVendorSourceTx, setCreateVendorSourceTx] = useState(null);

    // Secondary modal: apply the chosen vendor+category to other transactions that
    // share the same suggested vendor+category pair and are still unassigned/uncategorized.
    const [similarSuggestionsOpen, setSimilarSuggestionsOpen] = useState(false);
    const [similarSuggestionsLoading, setSimilarSuggestionsLoading] = useState(false);
    const [similarSuggestionsApplying, setSimilarSuggestionsApplying] = useState(false);
    const [similarSuggestionsCandidates, setSimilarSuggestionsCandidates] = useState([]);
    const [similarSuggestionsSelectedIds, setSimilarSuggestionsSelectedIds] = useState([]);
    const [similarSuggestionsContext, setSimilarSuggestionsContext] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [previousCursor, setPreviousCursor] = useState(null);
    const [currentCursor, setCurrentCursor] = useState(null);

    const [criticalTransactions, setCriticalTransactions] = useState([]);
    const [criticalCount, setCriticalCount] = useState(null);
    const [criticalLoading, setCriticalLoading] = useState(false);
    const [criticalNextCursor, setCriticalNextCursor] = useState(null);
    const [criticalPreviousCursor, setCriticalPreviousCursor] = useState(null);
    const [criticalCurrentCursor, setCriticalCurrentCursor] = useState(null);

    const [monthlySummary, setMonthlySummary] = useState({
        cashOnHand: 0,
        totalDeposits: 0,
        totalExpenses: 0,
        dateAfter: null,
        dateBefore: null,
        cashOnHandDeltaPct: 0,
        totalDepositsDeltaPct: 0,
        totalExpensesDeltaPct: 0,
    });

    const formatAccountLabel = useCallback((account) => {
        return `${account?.name || ""}`.trim();
    }, []);

    const pollTimerRef = useRef(null);
    const suspendPollingUntilRef = useRef(0);
    const editingRowIdRef = useRef(null);
    const savingEditRef = useRef(false);
    const transactionsRequestSeqRef = useRef(0);
    const criticalRequestSeqRef = useRef(0);
    useEffect(() => {
        editingRowIdRef.current = editingRowId;
    }, [editingRowId]);

    useEffect(() => {
        savingEditRef.current = savingEdit;
    }, [savingEdit]);

    const resetSimilarSuggestionsModal = useCallback(() => {
        setSimilarSuggestionsOpen(false);
        setSimilarSuggestionsLoading(false);
        setSimilarSuggestionsApplying(false);
        setSimilarSuggestionsCandidates([]);
        setSimilarSuggestionsSelectedIds([]);
        setSimilarSuggestionsContext(null);
    }, []);

    const resolveCursorParam = useCallback((cursorUrl) => {
        if (!cursorUrl) return null;
        try {
            const base =
                typeof window !== "undefined" && window?.location?.origin
                    ? window.location.origin
                    : "http://localhost";
            const urlObj = new URL(cursorUrl, base);
            return urlObj.searchParams.get("cursor");
        } catch {
            return null;
        }
    }, []);

    // Debounce the freeform search input so we don't refetch on every keystroke.
    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearchTerm(String(searchTerm || "").trim());
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    const getPeriodRange = useCallback((selectedPeriod) => {
        if (!selectedPeriod || selectedPeriod === "all") {
            return { date_after: null, date_before: null };
        }

        const today = new Date();
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        let start = null;

        const subtractMonths = (months) => {
            const d = new Date(end);
            d.setMonth(d.getMonth() - months);
            return d;
        };

        switch (selectedPeriod) {
            case "last_month":
                start = subtractMonths(1);
                break;
            case "last_3_months":
                start = subtractMonths(3);
                break;
            case "last_6_months":
                start = subtractMonths(6);
                break;
            case "last_year":
                // Use the previous full calendar year (Jan 1 -> Dec 31),
                // not a rolling "past 12 months" range.
                return {
                    date_after: `${today.getFullYear() - 1}-01-01`,
                    date_before: `${today.getFullYear() - 1}-12-31`,
                };
            case "past_12_months":
                // Rolling 12-month window ending today.
                start = subtractMonths(12);
                break;
            default:
                start = null;
        }

        const toDateString = (value) => value.toISOString().slice(0, 10);
        return {
            date_after: start ? toDateString(start) : null,
            date_before: toDateString(end),
        };
    }, []);

    // Resolve customer id from params or stored selection; stay on the same URL.
    // Also listen for customer changes (layout/selector dispatches an event).
    useEffect(() => {
        const syncFromStorage = () => {
            if (customer_id) {
                setResolvedCustomerId(customer_id);
                return;
            }

            const storedId = sessionStorage.getItem("selectedCustomerId");
            if (storedId) {
                setResolvedCustomerId(storedId);
            }
        };

        syncFromStorage();
        window.addEventListener("selectedCustomerId-changed", syncFromStorage);
        return () => {
            window.removeEventListener("selectedCustomerId-changed", syncFromStorage);
        };
    }, [customer_id, navigate]);

    // Fetch lookup tables needed to persist edits (vendor IDs + global accounts in the NEW namespace).
    useEffect(() => {
        if (!resolvedCustomerId) return;

        const fetchLookups = async () => {
            try {
                setVendorListLoading(true);
                const vendorsRes = await axios.get(`/api/v2/vendors/`, {
                    params: {
                        approval_status: "approved",
                        namespace: "new",
                        // Required so NamespaceMiddleware can resolve the correct namespace.
                        customer_id: resolvedCustomerId,
                        limit: 50,
                    },
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                });

                const vendorResults = vendorsRes.data?.results ?? vendorsRes.data ?? [];

                setVendors(Array.isArray(vendorResults) ? vendorResults : []);
                setVendorNextCursor(vendorsRes.data?.next ?? null);
            } catch (err) {
                console.error("Failed to load vendor/account lookups", err);
                setVendors([]);
                setVendorNextCursor(null);
            } finally {
                setVendorListLoading(false);
            }
        };

        fetchLookups();
    }, [resolvedCustomerId]);

    const fetchMoreVendors = useCallback(async () => {
        if (!resolvedCustomerId) return;
        if (!vendorNextCursor) return;
        if (vendorListLoading) return;

        try {
            setVendorListLoading(true);

            // CursorPagination returns an absolute next URL.
            // In the CRA dev setup we rely on a proxy for relative `/api/...` calls,
            // so fetching an absolute URL can bypass the proxy and fail.
            // Instead, extract the `cursor` param and re-hit the relative endpoint.
            const cursorParam = resolveCursorParam(vendorNextCursor);
            if (!cursorParam) return;

            const res = await axios.get(`/api/v2/vendors/`, {
                params: {
                    approval_status: "approved",
                    namespace: "new",
                    customer_id: resolvedCustomerId,
                    limit: 50,
                    cursor: cursorParam,
                },
                headers: { "Content-Type": "application/json" },
                withCredentials: true,
            });

            const nextResults = res.data?.results ?? res.data ?? [];
            const nextList = Array.isArray(nextResults) ? nextResults : [];

            setVendors((prev) => {
                const existing = Array.isArray(prev) ? prev : [];
                const byId = new Map(existing.map((v) => [String(v?.id), v]));
                nextList.forEach((v) => {
                    if (v?.id) byId.set(String(v.id), v);
                });
                return Array.from(byId.values());
            });

            setVendorNextCursor(res.data?.next ?? null);
        } catch (err) {
            console.error("Failed to load more vendors", err);
        } finally {
            setVendorListLoading(false);
        }
    }, [resolvedCustomerId, vendorNextCursor, vendorListLoading, resolveCursorParam]);

    // Vendor filter dropdown: server-side search + cursor pagination.
    useEffect(() => {
        if (!resolvedCustomerId) return;

        const term = String(vendorFilterInput || "").trim();
        if (!term) {
            setVendorFilterSearchOptions([]);
            setVendorFilterSearchNextCursor(null);
            setVendorFilterSearchLoading(false);
            return;
        }

        let isActive = true;
        const controller = new AbortController();
        const timeout = setTimeout(async () => {
            try {
                setVendorFilterSearchLoading(true);
                const res = await axios.get(`/api/v2/vendors/`, {
                    params: {
                        approval_status: "approved",
                        namespace: "new",
                        customer_id: resolvedCustomerId,
                        limit: 50,
                        name__icontains: term,
                    },
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                    signal: controller.signal,
                });

                const vendorResults = res.data?.results ?? res.data ?? [];
                if (isActive) {
                    setVendorFilterSearchOptions(
                        Array.isArray(vendorResults) ? vendorResults : []
                    );
                    setVendorFilterSearchNextCursor(res.data?.next ?? null);
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    console.error("Failed to search vendors (filter)", err);
                    if (isActive) {
                        setVendorFilterSearchOptions([]);
                        setVendorFilterSearchNextCursor(null);
                    }
                }
            } finally {
                if (isActive) setVendorFilterSearchLoading(false);
            }
        }, 250);

        return () => {
            isActive = false;
            clearTimeout(timeout);
            controller.abort();
        };
    }, [resolvedCustomerId, vendorFilterInput]);

    const fetchMoreVendorFilterOptions = useCallback(async () => {
        if (!resolvedCustomerId) return;

        const term = String(vendorFilterInput || "").trim();
        if (!term) {
            await fetchMoreVendors();
            return;
        }

        if (!vendorFilterSearchNextCursor) return;
        if (vendorFilterSearchLoading) return;

        try {
            setVendorFilterSearchLoading(true);

            // Same absolute-next-link issue as the base vendor lookup pagination.
            const cursorParam = resolveCursorParam(vendorFilterSearchNextCursor);
            if (!cursorParam) return;

            const res = await axios.get(`/api/v2/vendors/`, {
                params: {
                    approval_status: "approved",
                    namespace: "new",
                    customer_id: resolvedCustomerId,
                    limit: 50,
                    name__icontains: term,
                    cursor: cursorParam,
                },
                headers: { "Content-Type": "application/json" },
                withCredentials: true,
            });

            const vendorResults = res.data?.results ?? res.data ?? [];
            const nextList = Array.isArray(vendorResults) ? vendorResults : [];

            setVendorFilterSearchOptions((prev) => {
                const existing = Array.isArray(prev) ? prev : [];
                const byId = new Map(existing.map((v) => [String(v?.id), v]));
                nextList.forEach((v) => {
                    if (v?.id) byId.set(String(v.id), v);
                });
                return Array.from(byId.values());
            });

            setVendorFilterSearchNextCursor(res.data?.next ?? null);
        } catch (err) {
            console.error("Failed to load more vendors (filter)", err);
        } finally {
            setVendorFilterSearchLoading(false);
        }
    }, [resolvedCustomerId, vendorFilterInput, vendorFilterSearchNextCursor, vendorFilterSearchLoading, fetchMoreVendors, resolveCursorParam]);

    // When editing and the user types in the vendor field, query the vendor API so filtering
    // works across the full vendor dataset (not just the first page loaded).
    useEffect(() => {
        if (!resolvedCustomerId) return;
        if (!editingRowId) return;
        if (!vendorUserTyped) {
            setVendorSearchOptions([]);
            setVendorSearchLoading(false);
            return;
        }

        const term = String(editVendorInput || "").trim();
        if (!term) {
            setVendorSearchOptions([]);
            setVendorSearchLoading(false);
            return;
        }

        let isActive = true;
        const controller = new AbortController();
        const timeout = setTimeout(async () => {
            try {
                setVendorSearchLoading(true);
                const res = await axios.get(`/api/v2/vendors/`, {
                    params: {
                        approval_status: "approved",
                        namespace: "new",
                        // Required so NamespaceMiddleware can resolve the correct namespace.
                        customer_id: resolvedCustomerId,
                        // Keep this relatively high so common prefixes like "The" don't truncate
                        // away relevant results.
                        limit: 200,
                        name__icontains: term,
                    },
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                    signal: controller.signal,
                });

                const vendorResults = res.data?.results ?? res.data ?? [];
                if (isActive) {
                    setVendorSearchOptions(Array.isArray(vendorResults) ? vendorResults : []);
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    console.error("Failed to search vendors", err);
                    if (isActive) setVendorSearchOptions([]);
                }
            } finally {
                if (isActive) setVendorSearchLoading(false);
            }
        }, 250);

        return () => {
            isActive = false;
            clearTimeout(timeout);
            controller.abort();
        };
    }, [resolvedCustomerId, editingRowId, editVendorInput, vendorUserTyped]);

    // Bulk Edit vendor search (same behavior as row editor, but with its own state)
    useEffect(() => {
        if (!resolvedCustomerId) return;
        if (!bulkEditOpen) return;
        if (!bulkVendorUserTyped) {
            setBulkVendorSearchOptions([]);
            setBulkVendorSearchLoading(false);
            return;
        }

        const term = String(bulkVendorInput || "").trim();
        if (!term) {
            setBulkVendorSearchOptions([]);
            setBulkVendorSearchLoading(false);
            return;
        }

        let isActive = true;
        const controller = new AbortController();
        const timeout = setTimeout(async () => {
            try {
                setBulkVendorSearchLoading(true);
                const res = await axios.get(`/api/v2/vendors/`, {
                    params: {
                        approval_status: "approved",
                        namespace: "new",
                        customer_id: resolvedCustomerId,
                        limit: 200,
                        name__icontains: term,
                    },
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                    signal: controller.signal,
                });

                const vendorResults = res.data?.results ?? res.data ?? [];
                if (isActive) {
                    setBulkVendorSearchOptions(Array.isArray(vendorResults) ? vendorResults : []);
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    console.error("Failed to search vendors (bulk)", err);
                    if (isActive) setBulkVendorSearchOptions([]);
                }
            } finally {
                if (isActive) setBulkVendorSearchLoading(false);
            }
        }, 250);

        return () => {
            isActive = false;
            clearTimeout(timeout);
            controller.abort();
        };
    }, [resolvedCustomerId, bulkEditOpen, bulkVendorInput, bulkVendorUserTyped]);

    // Load accounts used as category override options.
    // IMPORTANT: Bank Activity categorization uses the 'new' namespace chart of accounts.
    // We must include BOTH:
    // - global accounts (customer_id = null)
    // - customer-specific accounts (customer_id = resolvedCustomerId)
    useEffect(() => {
        if (!resolvedCustomerId) return;

        const fetchGlobalAccounts = async () => {
            try {
                setGlobalAccountsLoading(true);
                setGlobalAccountsError(null);

                const accountsRes = await axios.get(
                    `/api/v2/customers/${resolvedCustomerId}/accounts/`,
                    {
                        // Force the accounts to come from the 'new' namespace, regardless of
                        // any ambient namespace context.
                        params: { namespace: 'new', leaf_only: true },
                        headers: { "Content-Type": "application/json" },
                        withCredentials: true,
                    }
                );


                const accountResults = accountsRes.data?.results ?? accountsRes.data ?? [];
                const normalized = Array.isArray(accountResults)
                    ? accountResults
                        .filter((a) => a && a.id && a.name)
                        .map((a) => ({
                            id: a.id,
                            name: a.name,
                            number: a.number ?? "",
                        }))
                    : [];

                setGlobalAccounts(normalized);
            } catch (err) {
                console.error("Failed to load global accounts", err);
                setGlobalAccounts([]);
                setGlobalAccountsError("Failed to load categories");
            } finally {
                setGlobalAccountsLoading(false);
            }
        };

        fetchGlobalAccounts();
    }, [resolvedCustomerId]);

    useEffect(() => {
        if (!resolvedCustomerId) return;
        const fetchData = async () => {
            try {
                setLoading(true);
                const plaidRes = await axios.get(
                    `/api/v2/customers/${resolvedCustomerId}/plaiditems`,
                    {
                        headers: { "Content-Type": "application/json" },
                        withCredentials: true,
                    }
                );

                // Backend returns a plain array (pagination disabled), fall back to [] if unexpected
                const plaidItems = Array.isArray(plaidRes.data) ? plaidRes.data : [];
                let nextAnyProcessing = false;

                const flattenedAccounts = [];
                plaidItems.forEach((item) => {
                    if (item?.transactions_processing_status === "processing") {
                        nextAnyProcessing = true;
                    }
                    (item.bankaccount_set || [])
                        .filter((account) => account.accounting_account)
                        .forEach((account) => {
                            flattenedAccounts.push({
                                ...account,
                                plaidItemName: item.name,
                            });
                        });
                });
                setAvailableBankAccounts(flattenedAccounts);
                setAnyProcessing(nextAnyProcessing);

                if (flattenedAccounts.length > 0) {
                    const cashId = flattenedAccounts[0]?.accounting_account?.id || null;
                    setSelectedBankCashAccountId(cashId);
                } else {
                    setTransactions([]);
                }
            } catch (err) {
                console.error("Failed to load bank activity data", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [resolvedCustomerId]);

    const fetchTransactions = useCallback(
        async ({ resetSelection = false, silent = false, cursorUrl = null } = {}) => {
            if (!resolvedCustomerId) return;

            // Don't clobber edits mid-flight.
            if (editingRowIdRef.current || savingEditRef.current) return;

            const requestSeq = ++transactionsRequestSeqRef.current;
            try {
                if (!silent) setLoading(true);
                const cursorParam = resolveCursorParam(cursorUrl);
                const { date_after, date_before } = getPeriodRange(periodFilter);
                const res = await axios.get(
                    `/api/v2/customers/${resolvedCustomerId}/transactions/`,
                    {
                        headers: { "Content-Type": "application/json" },
                        params: {
                            ...(cursorParam ? { cursor: cursorParam } : {}),
                            ordering: "-date",
                            with_classification: true,
                            include_journal_entries: false,
                            ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
                            ...(confidenceFilter && confidenceFilter !== "all"
                                ? { classification_level: confidenceFilter }
                                : {}),
                            ...(categoryFilter && categoryFilter !== "all"
                                ? { category_name: categoryFilter }
                                : {}),
                            ...(vendorFilter && vendorFilter !== "all"
                                ? { vendor_name: vendorFilter }
                                : {}),
                            ...(selectedBankAccountIds.length > 0
                                ? { bankaccount_ids: selectedBankAccountIds.join(",") }
                                : {}),
                            ...(date_after ? { date_after } : {}),
                            ...(date_before ? { date_before } : {}),
                        },
                        withCredentials: true,
                    }
                );
                const results = res.data.results ?? res.data;
                const list = Array.isArray(results) ? results : [];
                if (requestSeq !== transactionsRequestSeqRef.current) return;
                const stable = [...list].sort((a, b) => {
                    const ad = String(a?.date || "");
                    const bd = String(b?.date || "");
                    if (ad !== bd) return bd.localeCompare(ad);

                    const ac = String(a?.created_on || a?.order?.created_on || "");
                    const bc = String(b?.created_on || b?.order?.created_on || "");
                    if (ac !== bc) return bc.localeCompare(ac);

                    return String(b?.id || "").localeCompare(String(a?.id || ""));
                });
                setTransactions(stable);
                setNextCursor(res.data?.next ?? null);
                setPreviousCursor(res.data?.previous ?? null);
                setCurrentCursor(cursorUrl || null);
                if (resetSelection) setSelectedRows([]);
            } catch (err) {
                console.error("Failed to fetch transactions", err);
            } finally {
                if (!silent && requestSeq === transactionsRequestSeqRef.current) {
                    setLoading(false);
                }
            }
        },
        [
            periodFilter,
            getPeriodRange,
            resolveCursorParam,
            resolvedCustomerId,
            debouncedSearchTerm,
            confidenceFilter,
            categoryFilter,
            vendorFilter,
            selectedBankAccountIds,
        ]
    );

    const fetchCriticalTransactions = useCallback(
        async ({ resetSelection = false, silent = false, cursorUrl = null } = {}) => {
            if (!resolvedCustomerId) return;

            // Don't clobber edits mid-flight.
            if (editingRowIdRef.current || savingEditRef.current) return;

            const requestSeq = ++criticalRequestSeqRef.current;
            try {
                if (!silent) setCriticalLoading(true);
                const cursorParam = resolveCursorParam(cursorUrl);
                const { date_after, date_before } = getPeriodRange(periodFilter);

                const res = await axios.get(
                    `/api/v2/customers/${resolvedCustomerId}/transactions/`,
                    {
                        headers: { "Content-Type": "application/json" },
                        params: {
                            ...(cursorParam ? { cursor: cursorParam } : {}),
                            ordering: "-date",
                            with_classification: true,
                            include_journal_entries: false,
                            include_count: true,
                            classification_level: "Critical",
                            customer_id: resolvedCustomerId,
                            ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
                            ...(categoryFilter && categoryFilter !== "all"
                                ? { category_name: categoryFilter }
                                : {}),
                            ...(vendorFilter && vendorFilter !== "all"
                                ? { vendor_name: vendorFilter }
                                : {}),
                            ...(selectedBankAccountIds.length > 0
                                ? { bankaccount_ids: selectedBankAccountIds.join(",") }
                                : {}),
                            ...(date_after ? { date_after } : {}),
                            ...(date_before ? { date_before } : {}),
                        },
                        withCredentials: true,
                    }
                );

                const results = res.data.results ?? res.data;
                const list = Array.isArray(results) ? results : [];
                if (requestSeq !== criticalRequestSeqRef.current) return;
                const stable = [...list].sort((a, b) => {
                    const ad = String(a?.date || "");
                    const bd = String(b?.date || "");
                    if (ad !== bd) return bd.localeCompare(ad);

                    const ac = String(a?.created_on || a?.order?.created_on || "");
                    const bc = String(b?.created_on || b?.order?.created_on || "");
                    if (ac !== bc) return bc.localeCompare(ac);

                    return String(b?.id || "").localeCompare(String(a?.id || ""));
                });
                setCriticalTransactions(stable);
                setCriticalCount(
                    typeof res.data?.count === "number" ? res.data.count : null
                );
                setCriticalNextCursor(res.data?.next ?? null);
                setCriticalPreviousCursor(res.data?.previous ?? null);
                setCriticalCurrentCursor(cursorUrl || null);
                if (resetSelection) setSelectedRows([]);
            } catch (err) {
                console.error("Failed to fetch critical transactions", err);
            } finally {
                if (!silent && requestSeq === criticalRequestSeqRef.current) {
                    setCriticalLoading(false);
                }
            }
        },
        [
            periodFilter,
            getPeriodRange,
            resolveCursorParam,
            resolvedCustomerId,
            debouncedSearchTerm,
            categoryFilter,
            vendorFilter,
            selectedBankAccountIds,
        ]
    );

    const fetchCriticalCount = useCallback(
        async ({ silent = false } = {}) => {
            if (!resolvedCustomerId) return;

            // Don't clobber edits mid-flight.
            if (editingRowIdRef.current || savingEditRef.current) return;

            try {
                // This call is only used for the badge count; keep it lightweight.
                // We rely on include_count=true and a tiny page_size.
                if (!silent) setCriticalLoading(true);
                const { date_after, date_before } = getPeriodRange(periodFilter);

                const res = await axios.get(
                    `/api/v2/customers/${resolvedCustomerId}/transactions/`,
                    {
                        headers: { "Content-Type": "application/json" },
                        params: {
                            ordering: "-date",
                            with_classification: true,
                            include_journal_entries: false,
                            include_count: true,
                            page_size: 1,
                            classification_level: "Critical",
                            customer_id: resolvedCustomerId,
                            ...(selectedBankAccountIds.length > 0
                                ? { bankaccount_ids: selectedBankAccountIds.join(",") }
                                : {}),
                            ...(date_after ? { date_after } : {}),
                            ...(date_before ? { date_before } : {}),
                        },
                        withCredentials: true,
                    }
                );

                setCriticalCount(
                    typeof res.data?.count === "number" ? res.data.count : 0
                );
            } catch (err) {
                console.error("Failed to fetch critical transaction count", err);
            } finally {
                if (!silent) setCriticalLoading(false);
            }
        },
        [periodFilter, getPeriodRange, resolvedCustomerId, selectedBankAccountIds]
    );

    // Initial fetch / refetch when bank account changes.
    useEffect(() => {
        if (!resolvedCustomerId) return;
        if (tabValue === "critical") return;

        setNextCursor(null);
        setPreviousCursor(null);
        setCurrentCursor(null);

        fetchTransactions({ resetSelection: true, silent: false, cursorUrl: null });
        fetchCriticalCount({ silent: true });
    }, [resolvedCustomerId, periodFilter, debouncedSearchTerm, confidenceFilter, categoryFilter, vendorFilter, selectedBankAccountIds, tabValue, fetchTransactions, fetchCriticalCount]);

    // Fetch summary metrics for the top cards from the backend.
    useEffect(() => {
        if (!SHOW_BANK_ACTIVITY_METRICS) return;
        if (!resolvedCustomerId) return;

        const fetchSummary = async () => {
            try {
                const today = new Date();
                const endOfPrevMonth = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    0
                );
                const endOfTwoMonthsAgo = new Date(
                    today.getFullYear(),
                    today.getMonth() - 1,
                    0
                );

                const fetchSummaryAt = async (dateBefore) => {
                    const res = await axios.get(
                        `/api/v2/customers/${resolvedCustomerId}/transactions/summary/`,
                        {
                            params: {
                                customer_id: resolvedCustomerId,
                                date_before: formatIsoDateLocal(dateBefore),
                            },
                            headers: { "Content-Type": "application/json" },
                            withCredentials: true,
                        }
                    );
                    return res?.data || {};
                };

                const [todaySummary, prevMonthSummary, twoMonthsAgoSummary] =
                    await Promise.all([
                        fetchSummaryAt(today),
                        fetchSummaryAt(endOfPrevMonth),
                        fetchSummaryAt(endOfTwoMonthsAgo),
                    ]);

                // Deposits/expenses are cumulative totals up to date_before.
                // Derive month slices by differencing adjacent period endpoints.
                const thisMonthDeposits =
                    safeNumber(todaySummary.total_deposits) -
                    safeNumber(prevMonthSummary.total_deposits);
                const lastMonthDeposits =
                    safeNumber(prevMonthSummary.total_deposits) -
                    safeNumber(twoMonthsAgoSummary.total_deposits);

                const thisMonthExpenses =
                    safeNumber(todaySummary.total_expenses) -
                    safeNumber(prevMonthSummary.total_expenses);
                const lastMonthExpenses =
                    safeNumber(prevMonthSummary.total_expenses) -
                    safeNumber(twoMonthsAgoSummary.total_expenses);

                // Net cash flow this month = deposits - expenses.
                const thisMonthNetCashFlow = thisMonthDeposits - thisMonthExpenses;
                const lastMonthNetCashFlow = lastMonthDeposits - lastMonthExpenses;

                setMonthlySummary({
                    cashOnHand: thisMonthNetCashFlow,
                    totalDeposits: thisMonthDeposits,
                    totalExpenses: thisMonthExpenses,
                    dateAfter: formatIsoDateLocal(
                        new Date(today.getFullYear(), today.getMonth(), 1)
                    ),
                    dateBefore: formatIsoDateLocal(today),
                    cashOnHandDeltaPct: percentChange(
                        thisMonthNetCashFlow,
                        lastMonthNetCashFlow
                    ),
                    totalDepositsDeltaPct: percentChange(
                        thisMonthDeposits,
                        lastMonthDeposits
                    ),
                    totalExpensesDeltaPct: percentChange(
                        thisMonthExpenses,
                        lastMonthExpenses
                    ),
                });
            } catch (err) {
                console.error("Failed to fetch monthly summary", err);
                setMonthlySummary({
                    cashOnHand: 0,
                    totalDeposits: 0,
                    totalExpenses: 0,
                    dateAfter: null,
                    dateBefore: null,
                    cashOnHandDeltaPct: 0,
                    totalDepositsDeltaPct: 0,
                    totalExpensesDeltaPct: 0,
                });
            }
        };

        fetchSummary();
    }, [resolvedCustomerId]);

    // Fetch critical set on-demand when the critical tab is selected.
    useEffect(() => {
        if (tabValue !== "critical") return;
        if (!resolvedCustomerId) return;

        setCriticalTransactions([]);
        setCriticalNextCursor(null);
        setCriticalPreviousCursor(null);
        setCriticalCurrentCursor(null);

        fetchCriticalTransactions({ resetSelection: true, silent: false, cursorUrl: null });
    }, [tabValue, resolvedCustomerId, periodFilter, debouncedSearchTerm, categoryFilter, vendorFilter, selectedBankAccountIds, fetchCriticalTransactions]);

    // Poll the currently shown transactions every 5 seconds.
    useEffect(() => {
        if (!resolvedCustomerId) return;
        if (tabValue === "critical") return;

        // Clear any previous interval before starting a new one.
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
        }

        pollTimerRef.current = setInterval(() => {
            if (Date.now() < suspendPollingUntilRef.current) return;
            fetchTransactions({ resetSelection: false, silent: true, cursorUrl: currentCursor });
            fetchCriticalCount({ silent: true });
        }, 5000);

        return () => {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [resolvedCustomerId, fetchTransactions, fetchCriticalCount, currentCursor, tabValue]);

    const globalAccountNameSet = useMemo(() => {
        const names = new Set();
        (globalAccounts || []).forEach((account) => {
            if (account?.name) names.add(account.name);
        });
        return names;
    }, [globalAccounts]);

    const bankAccountOptions = useMemo(() => {
        const items = Array.isArray(availableBankAccounts) ? availableBankAccounts : [];
        const byId = new Map();
        items.forEach((account) => {
            if (!account?.id) return;
            const key = String(account.id);
            if (!byId.has(key)) byId.set(key, account);
        });
        return Array.from(byId.values()).sort((a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || ""))
        );
    }, [availableBankAccounts]);

    const bankAccountNameById = useMemo(() => {
        const lookup = new Map();
        bankAccountOptions.forEach((account) => {
            lookup.set(String(account.id), String(account.name || ""));
        });
        return lookup;
    }, [bankAccountOptions]);

    useEffect(() => {
        if (!selectedBankAccountIds.length) return;
        const valid = new Set(bankAccountOptions.map((account) => String(account.id)));
        setSelectedBankAccountIds((prev) =>
            prev.filter((id) => valid.has(String(id)))
        );
    }, [bankAccountOptions, selectedBankAccountIds.length]);

    const transactionsWithMeta = useMemo(() => {
        return (transactions || []).map((transaction) => {
            const acceptedVendorName = transaction.vendor_name || null;
            const acceptedVendorDisplay = acceptedVendorName || "Unassigned";

            const rawCategory = transaction.category_name;
            const acceptedCategoryName =
                rawCategory && globalAccountNameSet.has(rawCategory)
                    ? rawCategory
                    : null;
            const acceptedCategoryDisplay = acceptedCategoryName || "Uncategorized";

            const suggestedVendorRaw =
                (transaction.classification_vendor_name || "").trim() || null;

            const suggestedVendorIsNew =
                typeof transaction.vendor_suggestion_is_new === "boolean"
                    ? transaction.vendor_suggestion_is_new
                    : null;

            const suggestedVendorExistingVendorId =
                transaction.vendor_suggestion_existing_vendor_id || null;

            const suggestedCategoryRaw =
                (transaction.classification_account_label || "").trim() || null;
            const suggestedCategoryName =
                suggestedCategoryRaw && globalAccountNameSet.has(suggestedCategoryRaw)
                    ? suggestedCategoryRaw
                    : null;

            const vendorIsSuggested =
                suggestionsEnabled &&
                !acceptedVendorName &&
                Boolean(suggestedVendorRaw);

            const categoryIsSuggested =
                suggestionsEnabled &&
                !acceptedCategoryName &&
                Boolean(suggestedCategoryName);

            const vendorName = vendorIsSuggested ? suggestedVendorRaw : acceptedVendorDisplay;
            const categoryName = categoryIsSuggested
                ? suggestedCategoryName
                : acceptedCategoryDisplay;

            const confidence =
                String(transaction.classification_level || "").trim() || "Pending";
            const bankAccountName =
                bankAccountNameById.get(String(transaction.bankaccount || "")) || "Unknown";

            return {
                ...transaction,
                // Display values
                categoryName,
                vendorName,
                confidence,
                bankAccountName,
                // Suggestion metadata (used for styling + editor prefill)
                vendorIsSuggested,
                categoryIsSuggested,
                suggestedVendorName: suggestedVendorRaw,
                suggestedCategoryName,
                // Backend-resolved vendor suggestion metadata
                suggestedVendorIsNew,
                suggestedVendorExistingVendorId,
            };
        });
    }, [transactions, globalAccountNameSet, bankAccountNameById]);

    const criticalTransactionsWithMeta = useMemo(() => {
        return (criticalTransactions || []).map((transaction) => {
            const acceptedVendorName = transaction.vendor_name || null;
            const acceptedVendorDisplay = acceptedVendorName || "Unassigned";

            const rawCategory = transaction.category_name;
            const acceptedCategoryName =
                rawCategory && globalAccountNameSet.has(rawCategory)
                    ? rawCategory
                    : null;
            const acceptedCategoryDisplay = acceptedCategoryName || "Uncategorized";

            const suggestedVendorRaw =
                (transaction.classification_vendor_name || "").trim() || null;

            const suggestedVendorIsNew =
                typeof transaction.vendor_suggestion_is_new === "boolean"
                    ? transaction.vendor_suggestion_is_new
                    : null;

            const suggestedVendorExistingVendorId =
                transaction.vendor_suggestion_existing_vendor_id || null;

            const suggestedCategoryRaw =
                (transaction.classification_account_label || "").trim() || null;
            const suggestedCategoryName =
                suggestedCategoryRaw && globalAccountNameSet.has(suggestedCategoryRaw)
                    ? suggestedCategoryRaw
                    : null;

            const vendorIsSuggested =
                suggestionsEnabled &&
                !acceptedVendorName &&
                Boolean(suggestedVendorRaw);

            const categoryIsSuggested =
                suggestionsEnabled &&
                !acceptedCategoryName &&
                Boolean(suggestedCategoryName);

            const vendorName = vendorIsSuggested ? suggestedVendorRaw : acceptedVendorDisplay;
            const categoryName = categoryIsSuggested
                ? suggestedCategoryName
                : acceptedCategoryDisplay;

            const confidence =
                String(transaction.classification_level || "").trim() || "Pending";
            const bankAccountName =
                bankAccountNameById.get(String(transaction.bankaccount || "")) || "Unknown";

            return {
                ...transaction,
                // Display values
                categoryName,
                vendorName,
                confidence,
                bankAccountName,
                // Suggestion metadata (used for styling + editor prefill)
                vendorIsSuggested,
                categoryIsSuggested,
                suggestedVendorName: suggestedVendorRaw,
                suggestedCategoryName,
                // Backend-resolved vendor suggestion metadata
                suggestedVendorIsNew,
                suggestedVendorExistingVendorId,
            };
        });
    }, [criticalTransactions, globalAccountNameSet, bankAccountNameById]);

    const lookupTxMetaById = useCallback(
        (id) => {
            const key = String(id || "");
            if (!key) return null;
            const all = ([]
                .concat(Array.isArray(transactionsWithMeta) ? transactionsWithMeta : [])
                .concat(
                    Array.isArray(criticalTransactionsWithMeta)
                        ? criticalTransactionsWithMeta
                        : []
                ));
            return all.find((t) => String(t?.id) === key) || null;
        },
        [transactionsWithMeta, criticalTransactionsWithMeta]
    );

    const maybeOpenSimilarSuggestionsModal = useCallback(
        async ({ sourceTx, mappingVendorId, mappingCategoryLabel } = {}) => {
            if (!resolvedCustomerId) return;
            if (!sourceTx) return;

            const suggestedVendor = String(sourceTx?.suggestedVendorName || "").trim();
            const suggestedCategory = String(sourceTx?.suggestedCategoryName || "").trim();

            // This UX only applies when we have a *complete* suggested pair,
            // and the user just set a vendor+category mapping.
            if (!suggestedVendor || !suggestedCategory) return;
            if (!mappingVendorId || !mappingCategoryLabel) return;

            try {
                setSimilarSuggestionsLoading(true);

                const res = await axios.get(
                    `/api/v2/customers/${resolvedCustomerId}/transactions/similar-suggestions-preview/`,
                    {
                        params: {
                            suggested_vendor: suggestedVendor,
                            suggested_category: suggestedCategory,
                            exclude_transaction_id: sourceTx?.id,
                            limit: 200,
                        },
                        headers: { "Content-Type": "application/json" },
                        withCredentials: true,
                    }
                );

                const candidates = Array.isArray(res?.data?.candidates)
                    ? res.data.candidates
                    : [];
                if (candidates.length === 0) return;

                setSimilarSuggestionsContext({
                    suggestedVendor,
                    suggestedCategory,
                    mappingVendorId: String(mappingVendorId),
                    mappingCategoryLabel: String(mappingCategoryLabel),
                });
                setSimilarSuggestionsCandidates(candidates);
                setSimilarSuggestionsSelectedIds(candidates.map((c) => String(c?.id)));
                setSimilarSuggestionsOpen(true);
            } catch (err) {
                console.error("Failed to preview similar suggestion candidates", err);
            } finally {
                setSimilarSuggestionsLoading(false);
            }
        },
        [resolvedCustomerId]
    );

    const downloadCsv = (rows, filename) => {
        const headers = ["Date", "Description", "Amount", "Vendor", "Category"];
        const escapeCell = (value) => {
            const s = value == null ? "" : String(value);
            if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
            return s;
        };
        const csv = [headers.join(",")]
            .concat(
                rows.map((row) =>
                    [
                        escapeCell(row.date),
                        escapeCell(row.description),
                        escapeCell(row.amount),
                        escapeCell(row.vendor),
                        escapeCell(row.category),
                    ].join(",")
                )
            )
            .join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportSelectedTransactions = useCallback(() => {
        const selected = new Set(selectedRows || []);
        if (selected.size === 0) return;

        const base =
            tabValue === "critical"
                ? criticalTransactionsWithMeta
                : transactionsWithMeta;

        const rows = (Array.isArray(base) ? base : [])
            .filter((t) => selected.has(t.id))
            .map((t) => ({
                date: t.date,
                description: t.description || t.name || "",
                amount: t.amount,
                vendor: t.vendorName || "",
                category: t.categoryName || "",
            }));

        downloadCsv(
            rows,
            `selected-transactions-${new Date().toISOString().slice(0, 10)}.csv`
        );
    }, [selectedRows, tabValue, criticalTransactionsWithMeta, transactionsWithMeta]);

    const exportTransactions = useCallback(async () => {
        if (!resolvedCustomerId) return;
        if (exporting) return;

        try {
            setExporting(true);
            const { date_after, date_before } = getPeriodRange(periodFilter);
            const all = [];
            let cursorUrl = null;
            let safety = 0;

            while (safety < 200) {
                safety += 1;
                const cursorParam = resolveCursorParam(cursorUrl);
                const res = await axios.get(
                    `/api/v2/customers/${resolvedCustomerId}/transactions/`,
                    {
                        headers: { "Content-Type": "application/json" },
                        params: {
                            ...(cursorParam ? { cursor: cursorParam } : {}),
                            ordering: "-date",
                            with_classification: true,
                            include_journal_entries: false,
                            page_size: 500,
                            ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
                            ...(tabValue !== "critical" && confidenceFilter && confidenceFilter !== "all"
                                ? { classification_level: confidenceFilter }
                                : {}),
                            ...(tabValue === "critical" ? { classification_level: "Critical" } : {}),
                            ...(categoryFilter && categoryFilter !== "all"
                                ? { category_name: categoryFilter }
                                : {}),
                            ...(vendorFilter && vendorFilter !== "all"
                                ? { vendor_name: vendorFilter }
                                : {}),
                            ...(selectedBankAccountIds.length > 0
                                ? { bankaccount_ids: selectedBankAccountIds.join(",") }
                                : {}),
                            ...(date_after ? { date_after } : {}),
                            ...(date_before ? { date_before } : {}),
                        },
                        withCredentials: true,
                    }
                );
                const results = res.data.results ?? res.data;
                if (Array.isArray(results)) {
                    all.push(...results);
                }
                cursorUrl = res.data?.next ?? null;
                if (!cursorUrl) break;
            }

            const withMeta = all.map((tx) => {
                const vendorName = tx.vendor_name || "Unassigned";
                const rawCategory = tx.category_name;
                const categoryName =
                    rawCategory && globalAccountNameSet.has(rawCategory)
                        ? rawCategory
                        : "Uncategorized";
                const confidence =
                    String(tx.classification_level || "").trim() || "Pending";
                return {
                    ...tx,
                    vendorName,
                    categoryName,
                    confidence,
                };
            });

            const rows = withMeta.map((tx) => ({
                date: tx.date,
                description: tx.description ?? tx.name ?? "",
                amount: tx.amount,
                vendor: tx.vendorName,
                category: tx.categoryName,
            }));

            const after = date_after || "all";
            const before = date_before || "now";
            const filename = `bank-activity-export-${after}-to-${before}.csv`;
            downloadCsv(rows, filename);
        } catch (err) {
            console.error("Failed to export transactions", err);
        } finally {
            setExporting(false);
        }
    }, [
        resolvedCustomerId,
        exporting,
        getPeriodRange,
        periodFilter,
        resolveCursorParam,
        tabValue,
        globalAccountNameSet,
        debouncedSearchTerm,
        confidenceFilter,
        categoryFilter,
        vendorFilter,
        selectedBankAccountIds,
    ]);

    const globalAccountOptions = useMemo(() => {
        const items = Array.isArray(globalAccounts) ? globalAccounts : [];
        if (!selectedBankCashAccountId) return items;
        // Never offer the bank/cash account as an override option.
        return items
            .filter((a) => String(a?.id) !== String(selectedBankCashAccountId))
            .sort((a, b) =>
                formatAccountLabel(a).localeCompare(formatAccountLabel(b))
            );
    }, [globalAccounts, selectedBankCashAccountId, formatAccountLabel]);

    const categoryOptions = useMemo(() => {
        return (globalAccountOptions || []).map((opt) => opt.name);
    }, [globalAccountOptions]);

    // Apply category filter from URL query param
    useEffect(() => {
        const categoryParam = searchParams.get("category");
        if (categoryParam && categoryOptions.includes(categoryParam)) {
            setCategoryFilter(categoryParam);
        }
    }, [searchParams, categoryOptions]);

    // Apply vendor filter from URL query param
    useEffect(() => {
        const vendorParam = searchParams.get("vendor");
        if (vendorParam) {
            setVendorFilter(vendorParam);
        }
    }, [searchParams]);

    // Apply search term from URL query param
    useEffect(() => {
        const searchParam = searchParams.get("search");
        if (searchParam) {
            setSearchTerm(searchParam);
        }
    }, [searchParams]);

    const periodOptionLabels = useMemo(
        () => ({
            all: "All time",
            last_month: "Last month",
            last_3_months: "Last 3 months",
            last_6_months: "Last 6 months",
            past_12_months: "Past 12 months",
            last_year: "Last calendar year",
        }),
        []
    );

    const clearAllFilters = useCallback(() => {
        setSearchTerm("");
        setConfidenceFilter("all");
        setCategoryFilter("all");
        setVendorFilter("all");
        setVendorFilterInput("");
        setSelectedBankAccountIds([]);
        setPeriodFilter("all");
    }, []);

    const appliedFilters = useMemo(() => {
        const applied = [];
        const trimmedSearch = String(searchTerm || "").trim();
        if (trimmedSearch) {
            applied.push({
                key: "search",
                label: "Search",
                value: trimmedSearch,
                onClear: () => setSearchTerm(""),
            });
        }

        if (tabValue !== "critical" && confidenceFilter && confidenceFilter !== "all") {
            applied.push({
                key: "confidence",
                label: "Confidence",
                value: confidenceFilterOptionLabels[confidenceFilter] ?? confidenceFilter,
                onClear: () => setConfidenceFilter("all"),
            });
        }

        if (categoryFilter && categoryFilter !== "all") {
            applied.push({
                key: "category",
                label: "Category",
                value: categoryFilter,
                onClear: () => setCategoryFilter("all"),
            });
        }

        if (vendorFilter && vendorFilter !== "all") {
            applied.push({
                key: "vendor",
                label: "Vendor",
                value: vendorFilter,
                onClear: () => {
                    setVendorFilter("all");
                    setVendorFilterInput("");
                },
            });
        }

        if (selectedBankAccountIds.length > 0) {
            const selectedNames = selectedBankAccountIds
                .map((id) => bankAccountNameById.get(String(id)))
                .filter(Boolean);
            applied.push({
                key: "bankAccounts",
                label: "Bank Account",
                value:
                    selectedNames.length === 0
                        ? `${selectedBankAccountIds.length} selected`
                        : selectedNames.length <= 2
                            ? selectedNames.join(", ")
                            : `${selectedNames.length} selected`,
                onClear: () => setSelectedBankAccountIds([]),
            });
        }

        if (periodFilter && periodFilter !== "all") {
            applied.push({
                key: "period",
                label: "Period",
                value: periodOptionLabels[periodFilter] ?? periodFilter,
                onClear: () => setPeriodFilter("all"),
            });
        }

        return applied;
    }, [searchTerm, tabValue, confidenceFilter, categoryFilter, vendorFilter, selectedBankAccountIds, periodFilter, periodOptionLabels, bankAccountNameById]);

    const vendorLookupOptions = useMemo(() => {
        const fromApi = Array.isArray(vendors) ? vendors : [];
        const approvedFromApi = fromApi.filter((vendor) => vendor?.is_approved);
        return approvedFromApi;
    }, [vendors]);

    const vendorFilterOptionNames = useMemo(() => {
        const hasTerm = Boolean(String(vendorFilterInput || "").trim());
        const source = hasTerm ? vendorFilterSearchOptions : vendorLookupOptions;

        const names = [];
        (Array.isArray(source) ? source : []).forEach((vendor) => {
            const n = String(vendor?.name || "").trim();
            if (n) names.push(n);
        });

        if (vendorFilter && vendorFilter !== "all") {
            names.unshift(String(vendorFilter));
        }

        const seen = new Set();
        const deduped = [];
        names.forEach((name) => {
            const key = name.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            deduped.push(name);
        });

        return deduped;
    }, [vendorFilterInput, vendorFilterSearchOptions, vendorLookupOptions, vendorFilter]);

    const vendorAutocompleteOptions = useMemo(() => {
        const term = String(editVendorInput || "").trim().toLowerCase();
        const hasTerm = vendorUserTyped && Boolean(term);

        const source =
            hasTerm && Array.isArray(vendorSearchOptions) && vendorSearchOptions.length > 0
                ? vendorSearchOptions
                : vendorLookupOptions;

        // De-dupe and rank results so prefix matches appear first.
        const byId = new Map();
        (Array.isArray(source) ? source : []).forEach((vendor) => {
            if (!vendor?.id) return;
            if (!byId.has(String(vendor.id))) byId.set(String(vendor.id), vendor);
        });

        let items = Array.from(byId.values());
        if (hasTerm) {
            const rank = (name) => {
                const n = String(name || "").toLowerCase();
                if (!n) return 3;
                if (n.startsWith(term)) return 0;
                if (n.includes(term)) return 1;
                return 2;
            };

            items = items
                .filter((v) => rank(v?.name) < 2)
                .sort((a, b) => {
                    const ra = rank(a?.name);
                    const rb = rank(b?.name);
                    if (ra !== rb) return ra - rb;
                    return String(a?.name || "").localeCompare(String(b?.name || ""));
                });
        }

        // Restore the "create new vendor" affordance (only when the user actually typed).
        if (vendorUserTyped) {
            const input = String(editVendorInput || "").trim();
            if (input) {
                const inputLower = input.toLowerCase();
                const hasExactMatch = items.some(
                    (v) => String(v?.name || "").trim().toLowerCase() === inputLower
                );
                if (!hasExactMatch) {
                    items = [
                        {
                            id: "__create__",
                            name: "(create new vendor)",
                            __create: true,
                            inputValue: input,
                        },
                        ...items,
                    ];
                }
            }
        }

        // If a suggested "(new)" vendor sentinel is selected, ensure it's present
        // in options so MUI can render it reliably.
        if (editVendorOption?.__suggestedNew) {
            const alreadyIncluded = items.some(
                (v) => String(v?.id) === String(editVendorOption?.id)
            );
            if (!alreadyIncluded) {
                items = [editVendorOption, ...items];
            }
        }

        return items;
    }, [
        vendorLookupOptions,
        vendorSearchOptions,
        editVendorInput,
        vendorUserTyped,
        editVendorOption,
    ]);

    const bulkVendorAutocompleteOptions = useMemo(() => {
        const term = String(bulkVendorInput || "").trim().toLowerCase();
        const hasTerm = bulkVendorUserTyped && Boolean(term);

        const source =
            hasTerm && Array.isArray(bulkVendorSearchOptions) && bulkVendorSearchOptions.length > 0
                ? bulkVendorSearchOptions
                : vendorLookupOptions;

        const byId = new Map();
        (Array.isArray(source) ? source : []).forEach((vendor) => {
            if (!vendor?.id) return;
            if (!byId.has(String(vendor.id))) byId.set(String(vendor.id), vendor);
        });

        let items = Array.from(byId.values());
        if (hasTerm) {
            const rank = (name) => {
                const n = String(name || "").toLowerCase();
                if (!n) return 3;
                if (n.startsWith(term)) return 0;
                if (n.includes(term)) return 1;
                return 2;
            };

            items = items
                .filter((v) => rank(v?.name) < 2)
                .sort((a, b) => {
                    const ra = rank(a?.name);
                    const rb = rank(b?.name);
                    if (ra !== rb) return ra - rb;
                    return String(a?.name || "").localeCompare(String(b?.name || ""));
                });
        }

        return items;
    }, [vendorLookupOptions, bulkVendorSearchOptions, bulkVendorInput, bulkVendorUserTyped]);

    const filteredTransactions = useMemo(() => {
        return tabValue === "critical" ? criticalTransactionsWithMeta : transactionsWithMeta;
    }, [transactionsWithMeta, criticalTransactionsWithMeta, tabValue]);

    const tableLoading = tabValue === "critical" ? criticalLoading : loading;
    const activeNextCursor = tabValue === "critical" ? criticalNextCursor : nextCursor;
    const activePreviousCursor = tabValue === "critical" ? criticalPreviousCursor : previousCursor;
    const criticalBadgeCount =
        typeof criticalCount === "number" ? criticalCount : criticalTransactionsWithMeta.length;

    const metricCards = [
        {
            id: "cashOnHand",
            title: "Net Cash Flow (This Month)",
            value: monthlySummary.cashOnHand,
            delta: `${Math.abs(monthlySummary.cashOnHandDeltaPct).toFixed(1)}% last month`,
            icon: (
                <Box
                    component="img"
                    src="/subscription-cashflow.svg"
                    alt="Cash on Hand"
                    sx={{ width: 28, height: 28 }}
                />
            ),
            iconBg: "#E3F2FD",
            trendPositive: monthlySummary.cashOnHandDeltaPct >= 0,
        },
        {
            id: "totalDeposits",
            title: "Total Deposits",
            value: monthlySummary.totalDeposits,
            delta: `${Math.abs(monthlySummary.totalDepositsDeltaPct).toFixed(1)}% last month`,
            icon: (
                <Box
                    component="img"
                    src="/upward-green-arrow.svg"
                    alt="Total Deposits"
                    sx={{ width: 40, height: 40 }}
                />
            ),
            iconBg: "#E6F9F2",
            trendPositive: monthlySummary.totalDepositsDeltaPct >= 0,
        },
        {
            id: "totalExpenses",
            title: "Total Transactions",
            value: monthlySummary.totalExpenses,
            delta: `${Math.abs(monthlySummary.totalExpensesDeltaPct).toFixed(1)}% last month`,
            icon: (
                <Box
                    component="img"
                    src="/red-arrow-downward.svg"
                    alt="Total Expenses"
                    sx={{ width: 40, height: 40 }}
                />
            ),
            iconBg: "#FEEBEE",
            // For expenses/transactions, lower is better.
            trendPositive: monthlySummary.totalExpensesDeltaPct <= 0,
        },
    ];

    const filterFields = (confidenceFilter, categoryFilter, setConfidenceFilter, setCategoryFilter, categoryOptions) => ([
        {
            key: 'confidence',
            label: 'Confidence Level',
            value: confidenceFilter,
            setter: setConfidenceFilter,
            width: 251.33,
            options: [{ value: 'all', label: confidenceFilterOptionLabels.all }].concat(
                Object.keys(confidenceTokens).map((key) => ({
                    value: key,
                    label: confidenceFilterOptionLabels[key] ?? key,
                }))
            ),
        },
        {
            key: 'category',
            label: 'Category',
            value: categoryFilter,
            setter: setCategoryFilter,
            width: 251.33,
            options: [{ value: 'all', label: 'All Categories' }].concat(
                categoryOptions.map((opt) => ({ value: opt, label: opt }))
            ),
        },
    ]);

    const handleSelectRow = (transactionId) => {
        setSelectedRows((prev) =>
            prev.includes(transactionId) ? prev.filter((id) => id !== transactionId) : [...prev, transactionId]
        );
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRows(filteredTransactions.map((tx) => tx.id));
        } else {
            setSelectedRows([]);
        }
    };

    const beginEdit = (tx) => {
        // Always start the editor from what the user sees in the table.
        // This guarantees that "Save" persists what is displayed, without relying
        // on suggestion fields or stale editor state.
        setEditingRowId(tx.id);

        const normalizeName = (value) => String(value || "").trim().toLowerCase();

        // Vendor: prefer accepted vendor id. If none, use the displayed suggestion.
        let nextVendorOption = null;

        const acceptedVendorId = tx?.vendor ?? null;
        if (acceptedVendorId) {
            nextVendorOption =
                vendorLookupOptions.find((v) => String(v?.id) === String(acceptedVendorId)) ||
                null;
        }

        if (!nextVendorOption && tx?.vendorIsSuggested === true) {
            // Backend may tell us this suggestion resolves to an existing vendor (even if it is
            // not present in the paged vendorLookupOptions list).
            const suggestedExistingVendorId = tx?.suggestedVendorExistingVendorId ?? null;

            if (suggestedExistingVendorId) {
                nextVendorOption =
                    vendorLookupOptions.find(
                        (v) => String(v?.id) === String(suggestedExistingVendorId)
                    ) || {
                        id: suggestedExistingVendorId,
                        name: String(tx?.suggestedVendorName || "").trim() || "Vendor",
                    };
            } else {
                const suggestedNameRaw = String(tx?.suggestedVendorName || "").trim();
                const suggestedNameNorm = normalizeName(suggestedNameRaw);

                if (suggestedNameNorm) {
                    const existingByName = vendorLookupOptions.find(
                        (v) => normalizeName(v?.name) === suggestedNameNorm
                    );

                    nextVendorOption =
                        existingByName ||
                        {
                            id: "__suggested_new__",
                            name: `${suggestedNameRaw} (new)`,
                            __suggestedNew: true,
                            inputValue: suggestedNameRaw,
                        };
                }
            }
        }

        setEditVendorOption(nextVendorOption);
        setEditVendorInput(nextVendorOption?.name || "");
        setVendorUserTyped(false);

        // Category: prefer accepted category (by name). If none, use the displayed suggestion.
        let nextCategoryOption =
            globalAccountOptions.find((a) => String(a?.name) === String(tx?.category_name || "")) ||
            null;

        if (!nextCategoryOption && tx?.categoryIsSuggested === true) {
            const suggestedCategory = String(tx?.suggestedCategoryName || "").trim();
            if (suggestedCategory) {
                nextCategoryOption =
                    globalAccountOptions.find((a) => String(a?.name) === String(suggestedCategory)) ||
                    null;
            }
        }

        setEditCategoryOption(nextCategoryOption);
        setEditCategoryInput(nextCategoryOption ? formatAccountLabel(nextCategoryOption) : "");
        setCategoryUserTyped(false);
    };

    const openBulkEdit = () => {
        // Avoid mixing per-row editor state with bulk edit.
        if (editingRowId) cancelEdit();

        setBulkVendorOption(null);
        setBulkVendorInput("");
        setBulkVendorUserTyped(false);
        setBulkVendorSearchOptions([]);
        setBulkVendorSearchLoading(false);

        setBulkCategoryOption(null);
        setBulkCategoryInput("");
        setBulkCategoryUserTyped(false);
        setBulkSaving(false);

        setBulkEditOpen(true);
    };

    const closeBulkEdit = () => {
        setBulkEditOpen(false);
        setBulkVendorOption(null);
        setBulkVendorInput("");
        setBulkVendorUserTyped(false);
        setBulkVendorSearchOptions([]);
        setBulkVendorSearchLoading(false);
        setBulkCategoryOption(null);
        setBulkCategoryInput("");
        setBulkCategoryUserTyped(false);
        setBulkSaving(false);
    };

    const bulkCanConfirm = useMemo(() => {
        if (bulkSaving) return false;
        if (!selectedRows || selectedRows.length === 0) return false;
        const hasVendor = Boolean(bulkVendorOption?.id);
        const hasCategory = Boolean(bulkCategoryOption?.name);
        return hasVendor || hasCategory;
    }, [bulkSaving, selectedRows, bulkVendorOption, bulkCategoryOption]);

    const applyBulkEdit = async () => {
        if (!bulkCanConfirm) return;

        const payload = {};
        const nextVendorId = bulkVendorOption?.id ?? null;
        const nextCategoryLabel = bulkCategoryOption?.name ?? "";

        if (nextVendorId) {
            payload.vendor = nextVendorId;
        }
        if (nextCategoryLabel) {
            payload.category_label = nextCategoryLabel;
        }

        if (Object.keys(payload).length === 0) return;

        try {
            setBulkSaving(true);

            const bulkPayload = (selectedRows || []).map((id) => ({
                transaction_id: id,
                ...payload,
            }));

            const res = await axios.patch(`/api/v2/bulktransactions/`, bulkPayload, {
                params: {
                    with_classification: true,
                    include_journal_entries: false,
                    customer_id: resolvedCustomerId,
                },
                headers: { "Content-Type": "application/json" },
                withCredentials: true,
            });

            const updatedList = Array.isArray(res?.data) ? res.data : [];
            const updatesById = new Map(updatedList.map((t) => [String(t.id), t]));

            if (updatesById.size > 0) {
                setTransactions((prev) =>
                    (Array.isArray(prev) ? prev : []).map((t) => {
                        const updated = updatesById.get(String(t.id));
                        return updated ? { ...t, ...updated } : t;
                    })
                );

                setCriticalTransactions((prev) =>
                    (Array.isArray(prev) ? prev : []).map((t) => {
                        const updated = updatesById.get(String(t.id));
                        return updated ? { ...t, ...updated } : t;
                    })
                );

                suspendPollingUntilRef.current = Date.now() + 5500;
            }

            closeBulkEdit();

            if (tabValue === "critical") {
                fetchCriticalTransactions({
                    resetSelection: true,
                    silent: true,
                    cursorUrl: criticalCurrentCursor,
                });
            }
        } catch (err) {
            console.error("Failed to apply bulk edit", err);
        } finally {
            setBulkSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setEditVendorOption(null);
        setEditVendorInput("");
        setEditCategoryOption(null);
        setEditCategoryInput("");
        setSavingEdit(false);
    };

    const handleTabChange = (_, v) => {
        setTabValue(v);
        setSelectedRows([]);
    };

    const handleNextPage = () => {
        suspendPollingUntilRef.current = Date.now() + 6000;
        if (tabValue === "critical") {
            if (criticalNextCursor) {
                fetchCriticalTransactions({
                    resetSelection: true,
                    silent: false,
                    cursorUrl: criticalNextCursor,
                });
            }
            return;
        }

        if (nextCursor) {
            fetchTransactions({ resetSelection: true, silent: false, cursorUrl: nextCursor });
        }
    };

    const handlePreviousPage = () => {
        suspendPollingUntilRef.current = Date.now() + 6000;
        if (tabValue === "critical") {
            if (criticalPreviousCursor) {
                fetchCriticalTransactions({
                    resetSelection: true,
                    silent: false,
                    cursorUrl: criticalPreviousCursor,
                });
            }
            return;
        }

        if (previousCursor) {
            fetchTransactions({ resetSelection: true, silent: false, cursorUrl: previousCursor });
        }
    };

    const saveEdit = async (tx) => {
        try {
            // Save what is displayed in the editor selections (which are synced to the row display on edit start).
            // Only two writable fields for this screen:
            // - vendor (id)
            // - category_label (account name)
            const payload = {};

            // Vendor: create flow if user is saving a "(new)" sentinel.
            if (editVendorOption?.__suggestedNew === true && editVendorOption?.inputValue) {
                const rawName = String(editVendorOption.inputValue || "").trim();
                if (rawName) {
                    setCreateVendorName(rawName);
                    setCreateVendorAccount(editCategoryOption || null);
                    setCreateVendorSourceTx(tx);
                    setCreateVendorError(null);
                    setCreateVendorOpen(true);
                    return;
                }
            }

            // Explicit "(create new vendor)" choice
            if (editVendorOption?.__create === true || String(editVendorOption?.id || "") === "__create__") {
                const rawName = String(editVendorOption?.inputValue || editVendorInput || "").trim();
                if (rawName) {
                    setCreateVendorName(rawName);
                    setCreateVendorAccount(editCategoryOption || null);
                    setCreateVendorSourceTx(tx);
                    setCreateVendorError(null);
                    setCreateVendorOpen(true);
                    return;
                }
            }

            if (editVendorOption?.id && String(editVendorOption.id) !== "__suggested_new__") {
                payload.vendor = editVendorOption.id;
            }

            if (editCategoryOption?.name) {
                payload.category_label = String(editCategoryOption.name || "").trim();
            }

            if (Object.keys(payload).length === 0) {
                cancelEdit();
                return;
            }

            setSavingEdit(true);

            const res = await axios.patch(
                `/api/v2/customers/${resolvedCustomerId}/transactions/${tx.id}/`,
                payload,
                {
                    params: {
                        with_classification: true,
                        include_journal_entries: false,
                        customer_id: resolvedCustomerId,
                    },
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                }
            );

            const updated = res?.data || {};

            setTransactions((prev) =>
                (Array.isArray(prev) ? prev : []).map((t) =>
                    String(t.id) === String(tx.id) ? { ...t, ...updated } : t
                )
            );

            setCriticalTransactions((prev) =>
                (Array.isArray(prev) ? prev : []).map((t) =>
                    String(t.id) === String(tx.id) ? { ...t, ...updated } : t
                )
            );

            suspendPollingUntilRef.current = Date.now() + 5500;
            cancelEdit();

            // Secondary modal: offer to apply the chosen vendor+category mapping
            // to other unassigned transactions that share this row's suggestion pair.
            // IMPORTANT: use the pre-save row's suggestion metadata (`tx`) so we
            // don't lose the suggestion pair after manual classification logging.
            if (payload.vendor && payload.category_label) {
                void maybeOpenSimilarSuggestionsModal({
                    sourceTx: tx,
                    mappingVendorId: payload.vendor,
                    mappingCategoryLabel: payload.category_label,
                });
            }

            if (tabValue === "critical") {
                fetchCriticalTransactions({
                    resetSelection: true,
                    silent: true,
                    cursorUrl: criticalCurrentCursor,
                });
            } else {
                fetchCriticalCount({ silent: true });
            }
        } catch (err) {
            console.error("Failed to save transaction edits", err);
        } finally {
            setSavingEdit(false);
        }
    };

    const renderAmount = (amount) => {
        const isDebit = amount > 0;
        const color = isDebit ? "#E74C3C" : "#43A047";
        const Icon = isDebit ? ArrowDownwardIcon : ArrowUpwardIcon;
        return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Icon sx={{ width: 18, height: 18, color }} />
                <Typography
                    noWrap
                    sx={{
                        fontWeight: 600,
                        color,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {formatCurrency(Math.abs(amount))}
                </Typography>
            </Stack>
        );
    };

    const renderConfidenceChip = (confidence) => {
        const normalized = String(confidence || "").trim();
        const label = normalized || "Pending";
        const token = confidenceTokens[label] || confidenceTokens.Pending;
        return (
            <Chip
                label={label}
                size="small"
                sx={{
                    bgcolor: token.bg,
                    color: token.color,
                    fontWeight: 600,
                    borderRadius: '15px',
                    height: 30,
                    '& .MuiChip-label': { px: 2.4, fontSize: 14, lineHeight: '20px' },
                }}
            />
        );
    };

    const renderRow = (tx) => (
        <Box
            key={tx.id}
            sx={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #EEF0FD',
                backgroundColor: selectedRows.includes(tx.id) ? 'rgba(83,109,236,0.04)' : '#FFFFFF',
                transition: 'background-color 0.2s ease',
                minHeight: 64,
                '&:hover': {
                    backgroundColor: 'rgba(238,240,253,0.6)',
                },
            }}
        >
            <Box sx={{ width: 70, minWidth: 70, flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center' }}>
                <Checkbox checked={selectedRows.includes(tx.id)} onChange={() => handleSelectRow(tx.id)} sx={checkboxSx} />
            </Box>
            <Box sx={{ width: effectiveWidths['date'], minWidth: effectiveWidths['date'], flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{formatDate(tx.date)}</Typography>
            </Box>
            <Box sx={{ width: effectiveWidths['account'], minWidth: effectiveWidths['account'], flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, color: (tx.bankAccountName || tx.bankaccount_name) ? 'inherit' : '#9CA3AF' }}>
                    {tx.bankAccountName || tx.bankaccount_name || '—'}
                </Typography>
            </Box>
            <Box
                sx={{
                    width:      effectiveWidths['description'],
                    minWidth:   effectiveWidths['description'],
                    flexShrink: 0,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                }}
            >
                <Tooltip
                    title={tx.name || ""}
                    enterDelay={400}
                    arrow
                    slotProps={{
                        tooltip: {
                            sx: {
                                bgcolor: "#111827",
                                color: "#fff",
                                zIndex: 200002,
                            },
                        },
                        popper: {
                            sx: {
                                zIndex: 200002,
                            },
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            width: "100%",
                            minWidth: 0,
                            display: "block",
                            lineHeight: '20px',
                            cursor: 'default',
                        }}
                    >
                        {tx.name}
                    </Typography>
                </Tooltip>
            </Box>
            <Box sx={{ width: effectiveWidths['amount'], minWidth: effectiveWidths['amount'], flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                {renderAmount(tx.amount)}
            </Box>
            <Box sx={{ width: effectiveWidths['vendor'], minWidth: effectiveWidths['vendor'], flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                {editingRowId === tx.id ? (
                    <Autocomplete
                        PopperComponent={WiderCategoryPopper}
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        size="small"
                        value={editVendorOption}
                        inputValue={editVendorInput}
                        onInputChange={(_, value, reason) => {
                            if (reason === "input" || reason === "clear") {
                                setVendorUserTyped(reason === "input");
                                setEditVendorInput(value);
                            }
                        }}
                        onChange={(_, value) => {
                            if (value?.__create || String(value?.id) === "__create__") {
                                const initialName = (editVendorInput || "").trim();
                                setCreateVendorName(initialName);
                                setCreateVendorAccount(editCategoryOption || null);
                                setCreateVendorSourceTx(tx);
                                setCreateVendorError(null);
                                setCreateVendorOpen(true);
                                return;
                            }

                            setEditVendorOption(value || null);
                            setEditVendorInput(value?.name || "");
                            setVendorUserTyped(false);
                        }}
                        filterOptions={(options, state) => {
                            // Browse mode: show the first page of vendors and allow scrolling
                            // to paginate. Only filter/search when the user actually types.
                            if (!vendorUserTyped) return options;
                            // Options are already filtered/ranked in vendorAutocompleteOptions.
                            return options;
                        }}
                        options={vendorAutocompleteOptions}
                        loading={vendorUserTyped ? vendorSearchLoading : vendorListLoading}
                        loadingText={vendorUserTyped ? "Searching vendors…" : "Loading vendors…"}
                        getOptionLabel={(option) => option?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                            String(option?.id) === String(value?.id)
                        }
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: 1,
                                    borderRadius: '8px',
                                    boxShadow: '0px 4px 20px rgba(0,0,0,0.1), 0px 1px 3px rgba(0,0,0,0.12)',
                                    p: 0,
                                },
                            },
                            popper: { sx: { zIndex: 1400 } },
                            listbox: {
                                onScroll: (event) => {
                                    if (vendorUserTyped) return;
                                    const node = event.currentTarget;
                                    const atBottom =
                                        node.scrollTop + node.clientHeight >= node.scrollHeight - 24;
                                    if (atBottom) fetchMoreVendors();
                                },
                                sx: {
                                    p: 0,
                                    '& .MuiAutocomplete-option': {
                                        borderRadius: '5px',
                                        px: '10px',
                                        py: '8px',
                                        gap: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        transition: 'background-color 0.15s ease',
                                    },
                                    '& .MuiAutocomplete-option.Mui-focused': { backgroundColor: 'transparent' },
                                    '& .MuiAutocomplete-option.Mui-focusVisible': { backgroundColor: 'transparent' },
                                    '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: 'transparent' },
                                    '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused:hover, & .MuiAutocomplete-option.Mui-focusVisible:hover, & .MuiAutocomplete-option[aria-selected="true"]:hover': {
                                        backgroundColor: 'var(--cyanPurple-50, #EEF0FD)',
                                        borderRadius: '5px',
                                    },
                                },
                            },
                        }}
                        sx={{ width: "100%", minWidth: 0 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={tx.vendorName || "Select Vendor"}
                                inputProps={{
                                    ...params.inputProps,
                                    onFocus: (event) => {
                                        setVendorUserTyped(false);
                                        setEditVendorInput(editVendorOption?.name || tx.vendorName || "");
                                        setTimeout(() => {
                                            const input = event.target;
                                            const end = input.value?.length ?? 0;
                                            if (input.setSelectionRange) {
                                                input.setSelectionRange(0, end);
                                            }
                                        }, 0);
                                    },
                                    onClick: (event) => {
                                        setVendorUserTyped(false);
                                        setEditVendorInput(editVendorOption?.name || tx.vendorName || "");
                                        setTimeout(() => {
                                            const input = event.target;
                                            const end = input.value?.length ?? 0;
                                            if (input.setSelectionRange) {
                                                input.setSelectionRange(0, end);
                                            }
                                        }, 0);
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: 36,
                                        borderRadius: 1,
                                        '& fieldset': { borderWidth: 1.5, borderColor: '#536DEC' },
                                        '&:hover fieldset': { borderColor: '#536DEC' },
                                        '&.Mui-focused fieldset': { borderColor: '#536DEC' },
                                    },
                                    '& .MuiOutlinedInput-input': { py: 0, fontSize: 14 },
                                    '& .MuiOutlinedInput-input::placeholder': {
                                        color: '#111827',
                                        opacity: 1,
                                    },
                                }}
                            />
                        )}
                    />
                ) : (
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#111827',
                            ...(tx.vendorIsSuggested ? suggestedTextSx : {}),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        {tx.vendorName}
                    </Typography>
                )}
            </Box>
            <Box sx={{ width: effectiveWidths['category'], minWidth: effectiveWidths['category'], flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                {editingRowId === tx.id ? (
                    <Autocomplete
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        size="small"
                        value={editCategoryOption}
                        inputValue={editCategoryInput}
                        onInputChange={(_, value, reason) => {
                            // Only track user input; ignore programmatic resets which can
                            // cause the inputValue to diverge from filtering expectations.
                            if (reason === "input" || reason === "clear") {
                                setCategoryUserTyped(reason === "input");
                                setEditCategoryInput(value);
                            }
                        }}
                        onChange={(_, value) => {
                            setEditCategoryOption(value || null);
                            setEditCategoryInput(value ? formatAccountLabel(value) : "");
                            setCategoryUserTyped(false);
                        }}
                        filterOptions={(options, state) => {
                            if (!categoryUserTyped) return options;
                            return options.filter((option) =>
                                formatAccountLabel(option)
                                    .toLowerCase()
                                    .includes(state.inputValue.toLowerCase())
                            );
                        }}
                        options={globalAccountOptions}
                        loading={globalAccountsLoading}
                        loadingText="Loading categories…"
                        noOptionsText={
                            globalAccountsError
                                ? globalAccountsError
                                : "No categories found"
                        }
                        getOptionLabel={(option) => formatAccountLabel(option)}
                        isOptionEqualToValue={(option, value) =>
                            String(option?.id) === String(value?.id)
                        }
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: 1,
                                    borderRadius: '8px',
                                    boxShadow: '0px 4px 20px rgba(0,0,0,0.1), 0px 1px 3px rgba(0,0,0,0.12)',
                                    p: 0,
                                },
                            },
                            popper: { sx: { zIndex: 1400 } },
                            listbox: {
                                sx: {
                                    p: 0,
                                    '& .MuiAutocomplete-option': {
                                        borderRadius: '5px',
                                        px: '10px',
                                        py: '8px',
                                        gap: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        transition: 'background-color 0.15s ease',
                                    },
                                    '& .MuiAutocomplete-option.Mui-focused': { backgroundColor: 'transparent' },
                                    '& .MuiAutocomplete-option.Mui-focusVisible': { backgroundColor: 'transparent' },
                                    '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: 'transparent' },
                                    '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused:hover, & .MuiAutocomplete-option.Mui-focusVisible:hover, & .MuiAutocomplete-option[aria-selected="true"]:hover': {
                                        backgroundColor: 'var(--cyanPurple-50, #EEF0FD)',
                                        borderRadius: '5px',
                                    },
                                },
                            },
                        }}
                        sx={{ width: "100%", minWidth: 0 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={
                                    tx.categoryName || "Select Category"
                                }
                                inputProps={{
                                    ...params.inputProps,
                                    onFocus: (event) => {
                                        setCategoryUserTyped(false);
                                        setEditCategoryInput(
                                            editCategoryOption
                                                ? formatAccountLabel(editCategoryOption)
                                                : tx.categoryName || ""
                                        );
                                        setTimeout(() => {
                                            const input = event.target;
                                            const end = input.value?.length ?? 0;
                                            if (input.setSelectionRange) {
                                                input.setSelectionRange(0, end);
                                            }
                                        }, 0);
                                    },
                                    onClick: (event) => {
                                        setCategoryUserTyped(false);
                                        setEditCategoryInput(
                                            editCategoryOption
                                                ? formatAccountLabel(editCategoryOption)
                                                : tx.categoryName || ""
                                        );
                                        setTimeout(() => {
                                            const input = event.target;
                                            const end = input.value?.length ?? 0;
                                            if (input.setSelectionRange) {
                                                input.setSelectionRange(0, end);
                                            }
                                        }, 0);
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: 36,
                                        borderRadius: 1,
                                        '& fieldset': { borderWidth: 1.5, borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#536DEC' },
                                        '&.Mui-focused fieldset': { borderColor: '#536DEC' },
                                    },
                                    '& .MuiOutlinedInput-input': { py: 0, fontSize: 14 },
                                    '& .MuiOutlinedInput-input::placeholder': {
                                        color: '#111827',
                                        opacity: 1,
                                    },
                                }}
                            />
                        )}
                    />
                ) : (
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            ...(tx.categoryIsSuggested ? suggestedTextSx : {}),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        {tx.categoryName}
                    </Typography>
                )}
            </Box>
            <Box sx={{ width: effectiveWidths['confidence'], minWidth: effectiveWidths['confidence'], flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center' }}>{renderConfidenceChip(tx.confidence)}</Box>

            <Box
                sx={{
                    width:      effectiveWidths['actions'],
                    minWidth:   effectiveWidths['actions'],
                    flexShrink: 0,
                    pl: 1.5,
                    pr: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                }}
            >
                {editingRowId === tx.id ? (
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            size="small"
                            onClick={cancelEdit}
                            disabled={savingEdit}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                border: '1.5px solid #D14343',
                                color: '#D14343',
                                bgcolor: 'transparent',
                                '&:hover': { bgcolor: 'transparent' },
                                '&.Mui-disabled': {
                                    color: '#F5B5B5',
                                    borderColor: '#F5B5B5',
                                    bgcolor: 'transparent',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => saveEdit(tx)}
                            disabled={savingEdit}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                border: '1.5px solid #3BA768',
                                color: '#3BA768',
                                bgcolor: 'transparent',
                                '&:hover': { bgcolor: 'transparent' },
                                '&.Mui-disabled': {
                                    color: '#A7F3D0',
                                    borderColor: '#A7F3D0',
                                    bgcolor: 'transparent',
                                },
                            }}
                        >
                            <CheckIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                ) : (
                    <Button
                        startIcon={<EditIcon sx={{ width: 18, height: 18 }} />}
                        onClick={() => beginEdit(tx)}
                        sx={{
                            color: '#111827',
                            fontWeight: 600,
                            fontSize: 14,
                            minWidth: 'auto',
                            textTransform: 'none',
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 3,
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.05)',
                                borderRadius: 3,
                            },
                            '&:focus-visible': {
                                backgroundColor: 'rgba(0,0,0,0.07)',
                                borderRadius: 3,
                            },
                        }}
                    >
                        Edit
                    </Button>
                )}
            </Box>
        </Box>
    );

    const renderHeader = () => (
        <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#EEF0FD', borderBottom: '1px solid #E0E4F4', minHeight: 56 }}>
            <Box sx={{ width: 70, minWidth: 70, flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center', height: 56 }}>
                <Checkbox
                    checked={filteredTransactions.length > 0 && selectedRows.length === filteredTransactions.length}
                    indeterminate={selectedRows.length > 0 && selectedRows.length < filteredTransactions.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    sx={checkboxSx}
                />
            </Box>
            {columns.map((col) => (
                <Box
                    key={col.key}
                    sx={{
                        width:      effectiveWidths[col.key],
                        minWidth:   effectiveWidths[col.key],
                        flexShrink: 0,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        height: 56,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827', userSelect: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {col.label}
                    </Typography>
                    {/* Drag-to-resize handle */}
                    <Box
                        onMouseDown={(e) => startResize(e, col.key)}
                        sx={{
                            position: 'absolute', right: 0, top: 0, bottom: 0,
                            width: 8, cursor: 'col-resize', zIndex: 2,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            '&:hover .resize-bar': { opacity: 1 },
                        }}
                    >
                        <Box className="resize-bar" sx={{ width: 2, height: '55%', bgcolor: 'rgba(83,109,236,0.7)', borderRadius: 1, opacity: 0.35, transition: 'opacity 0.15s', pointerEvents: 'none' }} />
                    </Box>
                </Box>
            ))}
        </Box>
    );

    return (
        <ClientDashboardLayout title={null} contentMaxWidth="100%" contentAlign="left">
            <Stack
                spacing={2}
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    pt: 0,
                    pb: 3,
                    fontFamily: "'Rethink Sans', Helvetica, sans-serif",
                    '& .MuiButton-root, & .MuiTypography-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiMenuItem-root, & .MuiChip-label, & .MuiTab-root, & .MuiSelect-select, & .MuiAutocomplete-input, & .MuiFormLabel-root, & .MuiListItemText-primary':
                        { fontFamily: "'Rethink Sans', Helvetica, sans-serif" },
                }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#111827' }}>Bank Activity</Typography>
                        {anyProcessing && (
                            <Chip
                                label="Pending processing"
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    backgroundColor: "rgba(83, 109, 236, 0.12)",
                                    color: "#536DEC",
                                }}
                            />
                        )}
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadOutlinedIcon />}
                            onClick={exportTransactions}
                            disabled={exporting || !resolvedCustomerId}
                            sx={{
                                height: 44,
                                px: 3,
                                borderRadius: '8px',
                                borderColor: 'rgba(83,109,236,0.5)',
                                color: '#536DEC',
                                bgcolor: '#FFFFFF',
                                textTransform: 'capitalize',
                                fontFamily: "'Rethink Sans', Helvetica",
                                fontSize: 13,
                                fontWeight: 500,
                                letterSpacing: '0.46px',
                                lineHeight: '22px',
                                '& .MuiButton-startIcon': {
                                    mr: 1,
                                    '& > *:nth-of-type(1)': { fontSize: 21.6 },
                                },
                                '&:hover': { borderColor: 'rgba(83,109,236,0.5)', bgcolor: '#FFFFFF' },
                            }}
                        >
                            Export
                        </Button>
                    </Stack>
                </Stack>

                <Divider sx={{ borderColor: '#E5E7EB' }} />

                {SHOW_BANK_ACTIVITY_METRICS && (
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: 2.5,
                        }}
                    >
                        {metricCards.map((metric) => {
                            const showDelta = Boolean(metric.delta);
                            const ChangeIcon = metric.trendPositive ? ArrowUpwardIcon : ArrowDownwardIcon;
                            const changeColor = metric.trendPositive ? '#1F7A4F' : '#B42318';
                            const changeBg = metric.trendPositive ? '#E6F9F2' : '#FEEBEE';
                            return (
                                <SummaryCard key={metric.id} sx={{ width: '100%' }}>
                                    <Stack direction="row" spacing={1.5} sx={{ p: 2.5 }} alignItems="center">
                                        <Box sx={{ width: 56, height: 56, flexShrink: 0, bgcolor: metric.iconBg, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {metric.icon}
                                        </Box>
                                        <Stack spacing={0.5} sx={{ flex: 1 }}>
                                            <Typography sx={{ color: '#6B7280', fontSize: 14 }}>{metric.title}</Typography>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#171923E6' }}>
                                                    {formatCurrency(metric.value)}
                                                </Typography>
                                                {showDelta && (
                                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.75, py: 0.25, bgcolor: changeBg, borderRadius: 1 }}>
                                                        <ChangeIcon sx={{ width: 16, height: 16, color: changeColor }} />
                                                        <Typography sx={{ fontSize: 13, color: changeColor }}>{metric.delta}</Typography>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </SummaryCard>
                            );
                        })}
                    </Box>
                )}

                <ContentPanel sx={{ minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                    <Box sx={{ borderBottom: "1px solid #E5E7EB", mb: 2 }}>
                        <StyledTabs value={tabValue} onChange={handleTabChange} sx={{ mb: 0 }}>
                            <StyledTab value="overview" label="Overview" />
                            <StyledTab
                                value="critical"
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography sx={{ fontWeight: 600 }}>Critical Transactions</Typography>
                                        {criticalBadgeCount > 0 && (
                                            <Box
                                                sx={{
                                                    minWidth: 22,
                                                    height: 18,
                                                    px: 0.75,
                                                    borderRadius: "10px",
                                                    bgcolor: "#EB5757",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Typography sx={{ color: "#FFFFFF", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
                                                    {criticalBadgeCount}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                }
                            />
                        </StyledTabs>
                    </Box>

                    {!showSelectionActionBar && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '2fr 1.2fr 1fr 1fr 1fr 1fr' },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <FormControl fullWidth>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', mb: 1 }}>Search</Typography>
                                <Autocomplete
                                    freeSolo
                                    options={[]}
                                    inputValue={searchTerm}
                                    onInputChange={(_, value) => setSearchTerm(value)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder="Search by description, vendor, or category"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    height: 40,
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E5E7EB' },
                                                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                                                    '&.Mui-focused fieldset': { borderColor: '#536DEC' },
                                                },
                                                '& .MuiOutlinedInput-input': { py: 0 },
                                                '& input::placeholder': { color: '#9CA3AF', opacity: 1 },
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>

                            <FormControl fullWidth>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', mb: 1 }}>Bank Account</Typography>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={bankAccountOptions}
                                    value={bankAccountOptions.filter((account) =>
                                        selectedBankAccountIds.includes(String(account.id))
                                    )}
                                    onChange={(_, value) => {
                                        const ids = (Array.isArray(value) ? value : []).map((account) =>
                                            String(account?.id)
                                        );
                                        setSelectedBankAccountIds(ids);
                                    }}
                                    getOptionLabel={(option) => String(option?.name || "")}
                                    isOptionEqualToValue={(option, value) =>
                                        String(option?.id) === String(value?.id)
                                    }
                                    renderOption={(props, option, { selected }) => (
                                        <Box component="li" {...props} key={option.id}>
                                            <Checkbox
                                                checked={selected}
                                                sx={{ mr: 1, p: 0.5 }}
                                            />
                                            <Typography sx={{ fontSize: 14 }}>
                                                {option.name}
                                            </Typography>
                                        </Box>
                                    )}
                                    renderTags={(value) => {
                                        if (!Array.isArray(value) || value.length === 0) return null;
                                        if (value.length === 1) {
                                            return <Chip size="small" label={value[0]?.name || "1 selected"} />;
                                        }
                                        return <Chip size="small" label={`${value.length} selected`} />;
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder={
                                                selectedBankAccountIds.length > 0
                                                    ? ""
                                                    : "All Accounts"
                                            }
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    height: 40,
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E5E7EB' },
                                                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                                                    '&.Mui-focused fieldset': { borderColor: '#536DEC' },
                                                },
                                                '& .MuiAutocomplete-inputRoot': {
                                                    flexWrap: 'nowrap',
                                                    overflow: 'hidden',
                                                },
                                                '& .MuiAutocomplete-tag': {
                                                    maxWidth: 'calc(100% - 48px)',
                                                },
                                                '& .MuiChip-label': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                },
                                                '& .MuiOutlinedInput-input': { py: 0 },
                                                '& input::placeholder': { color: '#9CA3AF', opacity: 1 },
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>

                            {filterFields(confidenceFilter, categoryFilter, setConfidenceFilter, setCategoryFilter, categoryOptions)
                                .filter((field) => !(tabValue === "critical" && field.key === "confidence"))
                                .map((field) => (
                                    <FormControl key={field.key} fullWidth>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', mb: 1 }}>{field.label}</Typography>
                                        <Select
                                            value={field.value}
                                            onChange={(e) => field.setter(e.target.value)}
                                            IconComponent={SelectChevronIcon}
                                            displayEmpty
                                            size="small"
                                            MenuProps={filterSelectMenuProps}
                                            sx={{
                                                bgcolor: '#FFFFFF',
                                                borderRadius: '8px',
                                                '& .MuiOutlinedInput-root': { height: 40 },
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#536DEC' },
                                                '& .MuiSelect-select': {
                                                    fontFamily: "'Rethink Sans', Helvetica",
                                                    fontSize: 14,
                                                    color: '#6B7280',
                                                    padding: '9px 12px',
                                                    textAlign: 'left',
                                                },
                                                '& .MuiSvgIcon-root': { width: 16, height: 16 },
                                            }}
                                        >
                                            {field.options.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ))}

                            <FormControl fullWidth>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', mb: 1 }}>Vendor</Typography>
                                <Autocomplete
                                    options={vendorFilterOptionNames}
                                    value={vendorFilter === "all" ? null : vendorFilter}
                                    onChange={(_, value) => {
                                        const next = String(value || "").trim();
                                        if (!next) {
                                            setVendorFilter("all");
                                            setVendorFilterInput("");
                                        } else {
                                            setVendorFilter(next);
                                        }
                                    }}
                                    onInputChange={(_, value, reason) => {
                                        if (reason === 'input') {
                                            setVendorFilterInput(value);
                                        }
                                        if (reason === 'clear') {
                                            setVendorFilterInput('');
                                        }
                                        // MUI fires `reset` when an option is selected.
                                        // Clear the search term so the dropdown returns to browse mode
                                        // (and doesn't leave vendorFilterInput stuck on a stale search).
                                        if (reason === 'reset') {
                                            setVendorFilterInput('');
                                        }
                                    }}
                                    loading={vendorListLoading || vendorFilterSearchLoading}
                                    ListboxProps={{
                                        onScroll: (event) => {
                                            const listboxNode = event.currentTarget;
                                            const nearBottom =
                                                listboxNode.scrollTop + listboxNode.clientHeight >=
                                                listboxNode.scrollHeight - 24;
                                            if (nearBottom) {
                                                fetchMoreVendorFilterOptions();
                                            }
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder="All Vendors"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    height: 40,
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E5E7EB' },
                                                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                                                    '&.Mui-focused fieldset': { borderColor: '#536DEC' },
                                                },
                                                '& .MuiOutlinedInput-input': { py: 0 },
                                                '& input::placeholder': { color: '#9CA3AF', opacity: 1 },
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>

                            <FormControl fullWidth>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', mb: 1 }}>Period</Typography>
                                <Select
                                    value={periodFilter}
                                    onChange={(e) => setPeriodFilter(e.target.value)}
                                    IconComponent={SelectChevronIcon}
                                    displayEmpty
                                    size="small"
                                    MenuProps={filterSelectMenuProps}
                                    sx={{
                                        bgcolor: '#FFFFFF',
                                        borderRadius: '8px',
                                        '& .MuiOutlinedInput-root': { height: 40 },
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#536DEC' },
                                        '& .MuiSelect-select': {
                                            fontFamily: "'Rethink Sans', Helvetica",
                                            fontSize: 14,
                                            color: '#6B7280',
                                            padding: '9px 12px',
                                            textAlign: 'left',
                                        },
                                        '& .MuiSvgIcon-root': { width: 16, height: 16 },
                                    }}
                                >
                                    <MenuItem value="all">All time</MenuItem>
                                    <MenuItem value="last_month">Last month</MenuItem>
                                    <MenuItem value="last_3_months">Last 3 months</MenuItem>
                                    <MenuItem value="last_6_months">Last 6 months</MenuItem>
                                    <MenuItem value="past_12_months">Past 12 months</MenuItem>
                                    <MenuItem value="last_year">Last calendar year</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {!showSelectionActionBar && appliedFilters.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                {appliedFilters.map((filter) => (
                                    <Stack key={filter.key} direction="row" spacing={1} alignItems="center">
                                        <Typography
                                            sx={{
                                                fontSize: 16,
                                                fontWeight: 400,
                                                lineHeight: 1.5,
                                                letterSpacing: "0.15px",
                                                color: "rgba(0,0,0,0.87)",
                                            }}
                                        >
                                            {filter.label}:
                                        </Typography>
                                        <Chip
                                            label={filter.value}
                                            size="small"
                                            onDelete={filter.onClear}
                                            deleteIcon={<CancelIcon sx={{ width: 24, height: 24 }} />}
                                            sx={{
                                                bgcolor: "rgba(0,0,0,0.08)",
                                                maxHeight: 24,
                                                minHeight: 24,
                                                borderRadius: "100px",
                                                color: "rgba(0,0,0,0.87)",
                                                "& .MuiChip-label": {
                                                    px: "6px",
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    lineHeight: "18px",
                                                    letterSpacing: "0.16px",
                                                    color: "rgba(0,0,0,0.87)",
                                                },
                                                "& .MuiChip-deleteIcon": {
                                                    color: "rgba(0,0,0,0.54)",
                                                    "&:hover": { color: "rgba(0,0,0,0.87)" },
                                                },
                                            }}
                                        />
                                    </Stack>
                                ))}
                            </Stack>

                            <Button
                                variant="text"
                                onClick={clearAllFilters}
                                startIcon={<DeleteIcon sx={{ width: 24, height: 24 }} />}
                                sx={{
                                    px: "11px",
                                    py: "8px",
                                    borderRadius: "8px",
                                    textTransform: "capitalize",
                                    fontWeight: 500,
                                    fontSize: 15,
                                    lineHeight: "26px",
                                    letterSpacing: "0.46px",
                                    color: "#D32F2F",
                                    "&:hover": { bgcolor: "rgba(211, 47, 47, 0.04)" },
                                }}
                            >
                                Clear All
                            </Button>
                        </Box>
                    )}

                    {showSelectionActionBar && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 1.5,
                                mt: "50px",
                                px: 0,
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    lineHeight: 0,
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        fontFamily: "'Rethink Sans', Helvetica",
                                        fontWeight: 600,
                                        fontSize: 16,
                                        lineHeight: 1.75,
                                        letterSpacing: "0.15px",
                                        color: "rgba(0,0,0,0.87)",
                                    }}
                                >
                                    {selectedRows.length}
                                </Box>{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        fontFamily: "'Rethink Sans', Helvetica",
                                        fontWeight: 400,
                                        fontSize: 14,
                                        lineHeight: 1.43,
                                        letterSpacing: "0.17px",
                                        color: "rgba(0,0,0,0.6)",
                                    }}
                                >
                                    Transactions Selected
                                </Box>
                            </Typography>

                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    variant="contained"
                                    startIcon={<Box component="img" src="/bulk-edit-icon.svg" alt="" />}
                                    onClick={openBulkEdit}
                                    disabled={bulkSaving}
                                    sx={{
                                        bgcolor: "#536DEC",
                                        color: "#FFFFFF",
                                        borderRadius: "8px",
                                        px: "10px",
                                        py: "4px",
                                        height: "36px",
                                        minHeight: "36px",
                                        minWidth: "unset",
                                        textTransform: "capitalize",
                                        fontFamily: "'Rethink Sans', Helvetica",
                                        fontSize: 13,
                                        fontWeight: 500,
                                        letterSpacing: "0.46px",
                                        lineHeight: "22px",
                                        boxShadow:
                                            "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
                                        "& .MuiButton-startIcon": {
                                            width: 16,
                                            height: 24,
                                            overflow: "hidden",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginLeft: 0,
                                            marginRight: "8px",
                                        },
                                        "& .MuiButton-startIcon img": {
                                            width: 24,
                                            height: 24,
                                            display: "block",
                                        },
                                        "& .MuiButton-startIcon > *:nth-of-type(1)": {
                                            transform: "translateX(-1px)",
                                        },
                                        "&:hover": {
                                            bgcolor: "#536DEC",
                                            boxShadow:
                                                "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
                                        },
                                        "&.Mui-disabled": {
                                            bgcolor: "rgba(0,0,0,0.12)",
                                            color: "rgba(0,0,0,0.38)",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    Bulk Edit
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<FileDownloadOutlinedIcon />}
                                    onClick={exportSelectedTransactions}
                                    disabled={exporting}
                                    sx={{
                                        borderRadius: "8px",
                                        px: "16px",
                                        py: "6px",
                                        height: "34px",
                                        minHeight: "34px",
                                        borderColor: "rgba(83,109,236,0.5)",
                                        color: "#536DEC",
                                        bgcolor: "#FFFFFF",
                                        textTransform: "capitalize",
                                        fontFamily: "'Rethink Sans', Helvetica",
                                        fontSize: 13,
                                        fontWeight: 500,
                                        letterSpacing: "0.46px",
                                        lineHeight: "22px",
                                        "& .MuiButton-startIcon": {
                                            mr: 1,
                                            "& > *:nth-of-type(1)": { fontSize: 21.6 },
                                        },
                                        "&:hover": {
                                            borderColor: "rgba(83,109,236,0.5)",
                                            bgcolor: "#FFFFFF",
                                        },
                                        "&.Mui-disabled": {
                                            borderColor: "rgba(0,0,0,0.12)",
                                            color: "rgba(0,0,0,0.38)",
                                        },
                                    }}
                                >
                                    Export
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    <Box
                        ref={tableContainerRef}
                        sx={{
                            border: '1px solid #E4E8F5',
                            borderRadius: 0,
                            width: '100%',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        {/* Inner box expands to the sum of all column widths so horizontal scroll works after resize */}
                        <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
                            {renderHeader()}
                            {tableLoading ? (
                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">Loading...</Typography>
                                </Box>
                            ) : filteredTransactions.length === 0 ? (
                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">No transactions found.</Typography>
                                </Box>
                            ) : (
                                filteredTransactions.map((tx) => renderRow(tx))
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handlePreviousPage}
                            disabled={!activePreviousCursor || tableLoading}
                        >
                            Prev
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleNextPage}
                            disabled={!activeNextCursor || tableLoading}
                        >
                            Next
                        </Button>
                    </Box>
                </ContentPanel>
            </Stack>

            <Dialog
                open={bulkEditOpen}
                onClose={(_, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                }}
                disableEscapeKeyDown
                sx={{ zIndex: 200010 }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(4px)',
                    },
                }}
                PaperProps={{
                    sx: {
                        width: 600,
                        maxWidth: 'calc(100vw - 32px)',
                        borderRadius: '16px',
                        border: '1px solid rgba(33,33,33,0.08)',
                        boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                        fontFamily: "'Rethink Sans', Helvetica, sans-serif",
                    },
                }}
            >
                <DialogTitle sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: "'Rethink Sans', Helvetica",
                                    fontWeight: 600,
                                    fontSize: 24,
                                    lineHeight: 1.334,
                                    color: 'rgba(0,0,0,0.87)',
                                }}
                            >
                                Bulk Edit
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontFamily: "'Rethink Sans', Helvetica",
                                    fontWeight: 400,
                                    fontSize: 14,
                                    lineHeight: 1.43,
                                    letterSpacing: '0.17px',
                                    color: 'rgba(0,0,0,0.6)',
                                }}
                            >
                                You can initiate a bulk edit for multiple transactions seamlessly.
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={closeBulkEdit}
                            sx={{ p: '5px', borderRadius: '100px', mt: '2px' }}
                            aria-label="Close"
                        >
                            <CloseIcon sx={{ width: 20, height: 20 }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ px: 3, pb: 3 }}>
                        <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: "'Rethink Sans', Helvetica",
                                        fontWeight: 600,
                                        fontSize: 14,
                                        lineHeight: 1.57,
                                        letterSpacing: '0.1px',
                                        color: '#374151',
                                        mb: 0.5,
                                    }}
                                >
                                    Vendor
                                </Typography>
                                <Autocomplete
                                    size="small"
                                    value={bulkVendorOption}
                                    inputValue={bulkVendorInput}
                                    onInputChange={(_, value, reason) => {
                                        if (reason === 'input' || reason === 'clear') {
                                            setBulkVendorUserTyped(reason === 'input');
                                            setBulkVendorInput(value);
                                        }
                                    }}
                                    onChange={(_, value) => {
                                        setBulkVendorOption(value || null);
                                        setBulkVendorInput(value?.name || '');
                                        setBulkVendorUserTyped(false);
                                    }}
                                    filterOptions={(options) => {
                                        if (!bulkVendorUserTyped) return options;
                                        return options;
                                    }}
                                    options={bulkVendorAutocompleteOptions}
                                    loading={bulkVendorUserTyped ? bulkVendorSearchLoading : vendorListLoading}
                                    loadingText={bulkVendorUserTyped ? 'Searching vendors…' : 'Loading vendors…'}
                                    getOptionLabel={(option) => option?.name || ''}
                                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                                    popupIcon={<SelectChevronIcon />}
                                    forcePopupIcon
                                    disableClearable
                                    slotProps={{
                                        popper: { sx: { zIndex: 200020 } },
                                        paper: {
                                            sx: {
                                                mt: 1,
                                                borderRadius: '8px',
                                                boxShadow:
                                                    '0px 4px 20px rgba(0,0,0,0.1), 0px 1px 3px rgba(0,0,0,0.12)',
                                                p: 0,
                                            },
                                        },
                                        listbox: {
                                            onScroll: (event) => {
                                                if (bulkVendorUserTyped) return;
                                                const node = event.currentTarget;
                                                const atBottom =
                                                    node.scrollTop + node.clientHeight >= node.scrollHeight - 24;
                                                if (atBottom) fetchMoreVendors();
                                            },
                                            sx: {
                                                p: 0,
                                                '& .MuiAutocomplete-option': {
                                                    borderRadius: '5px',
                                                    px: '10px',
                                                    py: '8px',
                                                    gap: '10px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    transition: 'background-color 0.15s ease',
                                                },
                                                '& .MuiAutocomplete-option.Mui-focused': { backgroundColor: 'transparent' },
                                                '& .MuiAutocomplete-option.Mui-focusVisible': { backgroundColor: 'transparent' },
                                                '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: 'transparent' },
                                                '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused:hover, & .MuiAutocomplete-option.Mui-focusVisible:hover, & .MuiAutocomplete-option[aria-selected="true"]:hover': {
                                                    backgroundColor: 'var(--cyanPurple-50, #EEF0FD)',
                                                    borderRadius: '5px',
                                                },
                                            },
                                        },
                                    }}
                                    sx={{ width: '100%', minWidth: 0 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Vendor"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#536DEC' },
                                                    '&.Mui-focused fieldset': { borderColor: '#536DEC' },
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    fontSize: 14,
                                                    padding: '8px 12px',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: "'Rethink Sans', Helvetica",
                                        fontWeight: 600,
                                        fontSize: 14,
                                        lineHeight: 1.57,
                                        letterSpacing: '0.1px',
                                        color: '#374151',
                                        mb: 0.5,
                                    }}
                                >
                                    Category
                                </Typography>
                                <Autocomplete
                                    size="small"
                                    value={bulkCategoryOption}
                                    inputValue={bulkCategoryInput}
                                    onInputChange={(_, value, reason) => {
                                        if (reason === 'input' || reason === 'clear') {
                                            setBulkCategoryUserTyped(reason === 'input');
                                            setBulkCategoryInput(value);
                                        }
                                    }}
                                    onChange={(_, value) => {
                                        setBulkCategoryOption(value || null);
                                        setBulkCategoryInput(value ? formatAccountLabel(value) : '');
                                        setBulkCategoryUserTyped(false);
                                    }}
                                    filterOptions={(options, state) => {
                                        if (!bulkCategoryUserTyped) return options;
                                        return options.filter((option) =>
                                            formatAccountLabel(option)
                                                .toLowerCase()
                                                .includes(String(state.inputValue || '').toLowerCase())
                                        );
                                    }}
                                    options={globalAccountOptions}
                                    loading={globalAccountsLoading}
                                    loadingText="Loading categories…"
                                    noOptionsText={globalAccountsError ? globalAccountsError : 'No categories found'}
                                    getOptionLabel={(option) => formatAccountLabel(option)}
                                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                                    popupIcon={<SelectChevronIcon />}
                                    forcePopupIcon
                                    disableClearable
                                    slotProps={{
                                        popper: { sx: { zIndex: 200020 } },
                                        paper: {
                                            sx: {
                                                mt: 1,
                                                borderRadius: '8px',
                                                boxShadow:
                                                    '0px 4px 20px rgba(0,0,0,0.1), 0px 1px 3px rgba(0,0,0,0.12)',
                                                p: 0,
                                            },
                                        },
                                        listbox: {
                                            sx: {
                                                p: 0,
                                                '& .MuiAutocomplete-option': {
                                                    borderRadius: '5px',
                                                    px: '10px',
                                                    py: '8px',
                                                    gap: '10px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    transition: 'background-color 0.15s ease',
                                                },
                                                '& .MuiAutocomplete-option.Mui-focused': { backgroundColor: 'transparent' },
                                                '& .MuiAutocomplete-option.Mui-focusVisible': { backgroundColor: 'transparent' },
                                                '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: 'transparent' },
                                                '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused:hover, & .MuiAutocomplete-option.Mui-focusVisible:hover, & .MuiAutocomplete-option[aria-selected="true"]:hover': {
                                                    backgroundColor: 'var(--cyanPurple-50, #EEF0FD)',
                                                    borderRadius: '5px',
                                                },
                                            },
                                        },
                                    }}
                                    sx={{ width: '100%', minWidth: 0 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Category"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#536DEC' },
                                                    '&.Mui-focused fieldset': { borderColor: '#536DEC' },
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    fontSize: 14,
                                                    padding: '8px 12px',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: '20px',
                        py: '16px',
                        borderTop: '1px solid rgba(0,0,0,0.08)',
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={closeBulkEdit}
                        disabled={bulkSaving}
                        sx={{
                            borderRadius: '4px',
                            px: 2,
                            py: '6px',
                            borderColor: 'rgba(83,109,236,0.5)',
                            color: '#536DEC',
                            textTransform: 'capitalize',
                            fontFamily: "'Rethink Sans', Helvetica",
                            fontWeight: 500,
                            fontSize: 14,
                            letterSpacing: '0.4px',
                            lineHeight: '24px',
                            '&:hover': {
                                borderColor: 'rgba(83,109,236,0.7)',
                                bgcolor: 'transparent',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={applyBulkEdit}
                        disabled={!bulkCanConfirm}
                        sx={{
                            borderRadius: '4px',
                            px: 2,
                            py: '6px',
                            textTransform: 'capitalize',
                            fontFamily: "'Rethink Sans', Helvetica",
                            fontWeight: 500,
                            fontSize: 14,
                            letterSpacing: '0.4px',
                            lineHeight: '24px',
                            bgcolor: '#536DEC',
                            '&:hover': { bgcolor: '#4C63D7' },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(0,0,0,0.12)',
                                color: 'rgba(0,0,0,0.38)',
                            },
                        }}
                    >
                        {bulkSaving ? 'Confirming…' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={createVendorOpen}
                onClose={(_, reason) => {
                    // Only allow closing via explicit actions (Cancel button).
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                }}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
                // Ensure the dialog and its autocomplete popper render above high-z table overlays.
                sx={{ zIndex: 200010 }}
                PaperProps={{
                    sx: {
                        // Standard dialogs in the app use small radius via theme; don't override it.
                        // Also keep overflow visible so TextField labels aren't clipped.
                        overflow: 'visible',
                        fontFamily: "'Rethink Sans', Helvetica, sans-serif",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Create new vendor
                        </Typography>
                    </Box>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 3, overflow: 'visible' }}>
                    <Stack spacing={2}>
                        <TextField
                            label="Vendor name"
                            value={createVendorName}
                            onChange={(e) => setCreateVendorName(e.target.value)}
                            autoFocus
                            fullWidth
                            size="small"
                        />
                        <Autocomplete
                            size="small"
                            value={createVendorAccount}
                            onChange={(_, value) => setCreateVendorAccount(value || null)}
                            options={globalAccountOptions}
                            getOptionLabel={(option) => formatAccountLabel(option)}
                            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                            slotProps={{
                                popper: { sx: { zIndex: 200020 } },
                                paper: {
                                    sx: {
                                        mt: 1,
                                        borderRadius: '8px',
                                        boxShadow:
                                            '0px 4px 20px rgba(0,0,0,0.1), 0px 1px 3px rgba(0,0,0,0.12)',
                                        p: 0,
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category (account)"
                                    placeholder="Select category"
                                    size="small"
                                />
                            )}
                        />
                        {createVendorError ? (
                            <Typography sx={{ color: '#D14343', fontSize: 13 }}>{createVendorError}</Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setCreateVendorError(null);
                            setCreateVendorOpen(false);
                            setCreateVendorSourceTx(null);
                        }}
                        disabled={createVendorSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            const name = String(createVendorName || '').trim();
                            if (!name) {
                                setCreateVendorError('Vendor name is required.');
                                return;
                            }
                            if (!createVendorAccount?.id) {
                                setCreateVendorError('Category (account) is required.');
                                return;
                            }

                            try {
                                setCreateVendorSaving(true);
                                setCreateVendorError(null);

                                const formData = new FormData();
                                formData.append('name', name);
                                formData.append('account', String(createVendorAccount.id));
                                formData.append('is_active', 'true');
                                // Keep the vendor selectable immediately in the Bank Activity editor.
                                formData.append('is_approved', 'true');

                                const res = await axios.post(`/api/v2/vendors/?customer_id=${resolvedCustomerId}`, formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' },
                                    withCredentials: true,
                                });

                                const created = res?.data;
                                if (!created?.id) {
                                    throw new Error('Vendor create response missing id');
                                }

                                setVendors((prev) => {
                                    const list = Array.isArray(prev) ? prev : [];
                                    if (list.some((v) => String(v?.id) === String(created.id))) return list;
                                    return [created, ...list];
                                });

                                // Update the row editor selections to the newly created vendor + chosen category.
                                setEditVendorOption(created);
                                setEditVendorInput(created?.name || name);
                                setVendorUserTyped(false);

                                setEditCategoryOption(createVendorAccount);
                                setEditCategoryInput(formatAccountLabel(createVendorAccount));
                                setCategoryUserTyped(false);

                                setCreateVendorOpen(false);

                                // Auto-apply the created vendor + selected category to the currently edited transaction.
                                // This prevents the user from having to click "Save" again and ensures the row
                                // leaves Critical state immediately after creation.
                                const editingTxId = editingRowId;
                                if (editingTxId && resolvedCustomerId) {
                                    const payload = {
                                        vendor: created.id,
                                        category_label: String(createVendorAccount.name || ''),
                                    };

                                    const patchRes = await axios.patch(
                                        `/api/v2/customers/${resolvedCustomerId}/transactions/${editingTxId}/`,
                                        payload,
                                        {
                                            params: {
                                                with_classification: true,
                                                include_journal_entries: false,
                                                customer_id: resolvedCustomerId,
                                            },
                                            headers: { "Content-Type": "application/json" },
                                            withCredentials: true,
                                        }
                                    );

                                    const updated = patchRes?.data || {};

                                    setTransactions((prev) =>
                                        (Array.isArray(prev) ? prev : []).map((t) =>
                                            String(t.id) === String(editingTxId)
                                                ? { ...t, ...updated }
                                                : t
                                        )
                                    );

                                    setCriticalTransactions((prev) =>
                                        (Array.isArray(prev) ? prev : []).map((t) =>
                                            String(t.id) === String(editingTxId)
                                                ? { ...t, ...updated }
                                                : t
                                        )
                                    );

                                    // Avoid an immediate poll-driven overwrite.
                                    suspendPollingUntilRef.current = Date.now() + 5500;

                                    // If we're on the critical tab, silently refresh the list so it drops out
                                    // if it is no longer Critical.
                                    if (tabValue === "critical") {
                                        fetchCriticalTransactions({
                                            resetSelection: false,
                                            silent: true,
                                            cursorUrl: criticalCurrentCursor,
                                        });
                                    } else {
                                        // Keep the critical badge count in sync.
                                        fetchCriticalCount({ silent: true });
                                    }

                                    // Close the inline editor since the patch succeeded.
                                    cancelEdit();

                                    // Secondary modal: offer to apply the newly created vendor + chosen
                                    // category to other similar-suggestion transactions.
                                    void maybeOpenSimilarSuggestionsModal({
                                        sourceTx:
                                            createVendorSourceTx ||
                                            lookupTxMetaById(editingTxId),
                                        mappingVendorId: created.id,
                                        mappingCategoryLabel: String(
                                            createVendorAccount?.name || ''
                                        ),
                                    });

                                    setCreateVendorSourceTx(null);
                                }
                            } catch (err) {
                                console.error('Failed to create vendor', err);
                                setCreateVendorError('Failed to create vendor. Please try again.');
                            } finally {
                                setCreateVendorSaving(false);
                            }
                        }}
                        disabled={createVendorSaving}
                    >
                        {createVendorSaving ? 'Creating…' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={similarSuggestionsOpen}
                onClose={() => {
                    if (similarSuggestionsApplying) return;
                    resetSimilarSuggestionsModal();
                }}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        overflow: 'visible',
                        fontFamily: "'Rethink Sans', Helvetica, sans-serif",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Apply to other transactions?
                    </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2 }}>
                    {similarSuggestionsLoading ? (
                        <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                            Loading…
                        </Typography>
                    ) : (
                        <Stack spacing={1.5}>
                            <Typography sx={{ fontSize: 14, color: '#374151' }}>
                                {`Found ${similarSuggestionsCandidates.length} other unassigned transactions with the same suggestion.`}
                            </Typography>

                            <Box
                                sx={{
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                {(similarSuggestionsCandidates || []).map((row) => {
                                    const id = String(row?.id || '');
                                    const checked = similarSuggestionsSelectedIds.includes(id);
                                    const date = row?.date ? formatDate(row.date) : '—';
                                    const description = String(row?.description || '').trim() || '—';
                                    const amount = formatCurrency(Math.abs(Number(row?.amount || 0)));

                                    return (
                                        <Box
                                            key={id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 1.5,
                                                py: 1,
                                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                                '&:last-child': { borderBottom: 'none' },
                                            }}
                                        >
                                            <Checkbox
                                                checked={checked}
                                                disabled={similarSuggestionsApplying}
                                                onChange={() => {
                                                    setSimilarSuggestionsSelectedIds((prev) => {
                                                        const list = Array.isArray(prev) ? prev : [];
                                                        if (list.includes(id)) {
                                                            return list.filter((x) => x !== id);
                                                        }
                                                        return list.concat(id);
                                                    });
                                                }}
                                                sx={checkboxSx}
                                            />
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.25,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        color: '#111827',
                                                        width: 86,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {date}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        color: '#374151',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                    title={description}
                                                >
                                                    {description}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        color: '#111827',
                                                        width: 88,
                                                        flexShrink: 0,
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {amount}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => resetSimilarSuggestionsModal()}
                        disabled={similarSuggestionsApplying}
                    >
                        Skip
                    </Button>
                    <Button
                        variant="contained"
                        disabled={
                            similarSuggestionsApplying ||
                            similarSuggestionsLoading ||
                            !similarSuggestionsContext ||
                            similarSuggestionsSelectedIds.length === 0
                        }
                        onClick={async () => {
                            if (!similarSuggestionsContext) return;
                            try {
                                setSimilarSuggestionsApplying(true);

                                await axios.post(
                                    `/api/v2/customers/${resolvedCustomerId}/transactions/similar-suggestions-apply/`,
                                    {
                                        suggested_vendor:
                                            similarSuggestionsContext.suggestedVendor,
                                        suggested_category:
                                            similarSuggestionsContext.suggestedCategory,
                                        transaction_ids: similarSuggestionsSelectedIds,
                                        vendor: similarSuggestionsContext.mappingVendorId,
                                        category_label:
                                            similarSuggestionsContext.mappingCategoryLabel,
                                    },
                                    {
                                        headers: { "Content-Type": "application/json" },
                                        withCredentials: true,
                                    }
                                );

                                suspendPollingUntilRef.current = Date.now() + 5500;
                                resetSimilarSuggestionsModal();

                                if (tabValue === 'critical') {
                                    fetchCriticalTransactions({
                                        resetSelection: false,
                                        silent: true,
                                        cursorUrl: criticalCurrentCursor,
                                    });
                                } else {
                                    fetchTransactions({
                                        resetSelection: false,
                                        silent: true,
                                        cursorUrl: currentCursor,
                                    });
                                    fetchCriticalCount({ silent: true });
                                }
                            } catch (err) {
                                console.error(
                                    'Failed to apply mapping to similar transactions',
                                    err
                                );
                            } finally {
                                setSimilarSuggestionsApplying(false);
                            }
                        }}
                    >
                        {similarSuggestionsApplying
                            ? 'Applying…'
                            : `Apply to ${similarSuggestionsSelectedIds.length}`}
                    </Button>
                </DialogActions>
            </Dialog>

        </ClientDashboardLayout>
    );
};

export default BankActivity;
